import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useTheme } from '@mui/material/styles';
import './SearchArea.css';
import { apiClient } from '../../../lib/apiClient';
import { fetchLatestWeeklyReportPublicSettings } from '../../../api/weeklyReportPublicSettings';
import {
  subscribeWeeklyReportPublicScopeRefresh,
  type WeeklyReportPublicScopeRefreshReason,
} from '../../../lib/weeklyReportPublicScopeRefresh';
import type { WeeklyReportPublicSettingRow } from '../../../lib/weeklyReportPublicScopeDisplay';
import SearchArea1st from './SearchArea1st';
import OverwriteModal, { HistoryItem } from '../OverwriteModal/OverwriteModal';
import OverwriteInputSearchArea, {
  SearchSelectionPayload,
} from './OverwriteInputSearchArea';
import { OptionsSelectList } from '../../../state/master';
import { useUserProfile } from '../../../context/UserProfileContext';
import { USER_REGISTERED_EVENT } from '../../../lib/userRegistrationEvent';

import * as SearchAreaUtils from '../../common/utils/SearchArea.utils';
import type {
  LastNewDraftCache,
  LastNewDraftField,
} from '../../common/utils/SearchArea.utils';
import {LockWarningBar} from '../../common/lock/LockWarningBar';
import { shareInFlightRequest } from '../../../lib/sharedApiRequest';

import {
  DATE_CLEAR_GUARD_MS,
  LOADING_MESSAGE,
  OVERWRITE_SEARCH_CACHE_KEY_PREFIX,
} from './SearchArea.constants';
import {
  CLASSIFICATION_MARKET_TARGET_ATTR_NAME,
  FIXED_ATTR_NAME_PROJECT,
  FIXED_ATTR_NAME_POWERTRAIN,
  FIXED_ATTR_NAME_SHIMUKE,
  FIXED_ATTR_NAME_LOT,
} from '../../../constants';

// 画面復帰（focus/visibility）でマスタを再取得する最小間隔
const MASTER_PASSIVE_REFRESH_MIN_INTERVAL_MS = 30_000;

/* =========================================================
 * 型定義
 * ========================================================= */

export type CognitoAttribute = {
  Name: string;
  Value: string;
};

export type AttrFromMaster = {
  attr_id: number;
  department_id: number;
  team_id: number;
  attr_name: string;
  label?: string;
  attr_required?: number | string | boolean;
  input_required?: number | string | boolean;
  attr_type?: number | string;
  multiple_select?: number | string;
  order_number?: number | string;
  status?: string | number | boolean;
  options?:
    | Array<{
        value?: string | number;
        label?: string;
        option_id?: number;
        department_id?: number;
        team_id?: number;
        status?: string | number | boolean;
      }>
    | null;
};

export type WeeklyMaster = {
  departments?: any[];
  teams?: any[];
  attributeManage?: AttrFromMaster[];
  attributes?: AttrFromMaster[];
  attrMap?: Record<string, number>;
  attributeBuckets?: Array<{ name: string; items: any[] }>;
  buckets?: Record<
    string,
    Array<{
      value?: string | number;
      label?: string;
      color?: string;
      [k: string]: any;
    }>
  >;
  creators?: Array<{ label: string; value?: string }>;
  approvers?: Array<{ label: string; value?: string }>;
  approverAMs?: Array<{ label: string; value?: string }>;
  taskMembers?: Array<{ label: string; value?: string }>;
  wrPublicSettings?: WeeklyReportPublicSettingRow[];
};

type Tab = 'new' | 'overwrite' | 'sectionName' | null;

type OverwriteModalCloseReason = 'cancel' | 'commit';

type OverwriteModalCloseOptions = {
  reason?: OverwriteModalCloseReason;
};

type UserInfoRow = {
  department: string | null;
  department_id?: number | null;
  first_name?: string | null;
  last_name?: string | null;
  mail_address: string;
  team?: string | null;
};

type TeamDepartmentLink = {
  team_id: number;
  team_label?: string;
  team_value?: string | null;
  department_id: number;
  order_number?: number | null;
};

// Overwrite 用のプリフィル
export type OverwritePrefill = {
  // camelCase 群
  wr_id?: number; //どの週報を元にしているか識別するためのID
  team?: string;
  reporter?: string;
  approver3?: string;
  approverAM?: string;
  reportType?: string;
  startDate?: string;
  endDate?: string;
  shikimuke?: string;
  lot?: string;
  project?: string;
  powertrain?: string;
  taskMembers?: Array<{ lastName: string; firstName: string; percent?: number }>;
  attributes?: Array<{ attr_id?: number; attr_name: string; attr_value: string }>;

  // 子が読む、フリーワードの項目、本命（{attr_id, attr_value}）
  attribute_values?:
    | Array<
        { attr_id?: number | string; attr_name?: string; attr_value?: string } | string
      >
    | string
    | null;

  // API レスポンスの別名も保持する。OverwriteInputSearchArea 側で値復元に使う。
  attributeValues?: Array<{ attr_id?: number | string; attr_name?: string; attr_value?: string }> | null;
  base_attributes?: Array<{ attr_id?: number | string; attr_name?: string; attr_value?: string }> | null;
  baseAttributes?: Array<{ attr_id?: number | string; attr_name?: string; attr_value?: string }> | null;
  weekly_report_base_attributes?: Array<{ attr_id?: number | string; attr_name?: string; attr_value?: string }> | null;

  // snake_case 群（OverwriteInputSearchArea で参照）
  start_date?: string;
  end_date?: string;
  create_by?: string;
  report_types?: Array<string | number>;
  departments?: number[];

  approver?: string;
  approve_am?: string;
  respondents?: string[] | Record<string, any> | string | null;
  respondents_1?: string[] | Record<string, any> | string | null;
  task_members?: any[] | Record<string, any> | string | null;
  members?: any[] | Record<string, any> | string | null;
  title?: string;
  body?: string;
};

/* =========================================================
 * Props
 * ========================================================= */

export interface SearchAreaProps {
  activeTab: 'new' | 'overwrite' | 'sectionName' | null;

  // activeTab の型と揃える（SearchArea1st / 親側との互換性確保）
  setActiveTab: (tab: 'new' | 'overwrite' | 'sectionName' | null) => void;

  // createBy に使う “氏名” を親に渡す
  onResolvedCreateByChange?: (createBy: string) => void;

  divisionOptions: OptionsSelectList[];
  division: string;
  setDivision: React.Dispatch<React.SetStateAction<string>>;

  team?: string;
  setTeam?: (value: string) => void;

  reportType?: string;
  setReportType?: (value: string) => void;

  approver3: string;
  setApprover3: (value: string) => void;

  approverAM: string;
  setApproverAM: (value: string) => void;

  reporter: string;
  setReporter: (value: string) => void;

  startDate: string;
  setStartDate: (d: string) => void;

  endDate: string;
  setEndDate: (d: string) => void;

  shikimuke: string;
  setShikimuke: (value: string) => void;

  lot: string;
  setLot: (value: string) => void;

  enlightSyncEnabled: boolean;

  onSearchResults: (results: any[]) => void;
  onLoadHistoryData: (item: HistoryItem) => void;
  onHistoryLoaded: (items: HistoryItem[]) => void;

  onDynamicFieldsChange?: (fields: any[]) => void;
  onFreeWordsChange?: (fw: Record<string, string>) => void;
  onAttributesChange?: (
    attrs: Array<{ attr_id: number; attr_name: string; attr_value: string }>
  ) => void;

  validationErrors?: {
    reporter?: string;
    startDate?: string;
    endDate?: string;
    title?: string;
    body?: string;
  };

  onResolvedIdsChange?: (ids: {
    departmentId: number | null;
    teamId: number | null;
  }) => void;
  onResolvedTeamDocsetIdChange?: (docsetId: number | null | undefined) => void;

  cognitoAttributes?: CognitoAttribute[];
  /** Overwrite モーダルを「閉じる」押下で通知（選択なし） */
  onOverwriteModalClosed?: (options?: OverwriteModalCloseOptions) => void;

  /** OverwriteInputSearchArea の表示値ペイロードを親(InputEdit)にリフトアップ */
  onSearchPayloadChange?: (payload: SearchSelectionPayload | null) => void;
  isLocked?: boolean;
  lockedByName?: string | null;
  onProvideClearNewDates?: (fn: () => void) => void;

  // InputEdit から渡される loginId（型エラー解消用）
  loginId?: string;
}

/* =========================================================
 * 定数 / ユーティリティ
 *   ※ SearchArea.utils.ts に分離
 * ========================================================= */

const {
  WEEKLY_INPUT_ITEMS_PATH,
  APPROVE3_MEMBERS_PATH,
  APPROVEAM_MEMBERS_PATH,

  normUpper,
  normalizeEmail,
  uniq,
  splitTokens,
  toNum,
  extractEmailFromCognito,
  readLastNewTeamCache,
  writeLastNewTeamCache,
  sanitizeDraftFields,
  patchDateRangeFieldInDraftFields,
  sanitizeTaskMembers,
  areTaskMembersEqual,
  sanitizeFreeWords,
  areFreeWordsEqual,
  hasAnyFreeWordsValue,
  readLastNewDraftCache,
  writeLastNewDraftCache,
  fetchUserInfoOnce,
  fetchByDeptWithCache,
  extractWeeklyBaseAttributes,
  resolveTeamIdByHint,
  normalizeTeamInPayload,
  applyTeamSelectionToPayload,
} = SearchAreaUtils;

/** =========================================================
 * 安定シリアライズ（キー順を固定）
 * ※ draft 程度のサイズならこれで十分
 *   - savedAt のような揺れる値は呼び出し側で除外してから渡すこと
 * ======================================================= */
const stableStringify = (value: unknown): string => {
  const seen = new WeakSet<object>();

  const normalize = (v: any): any => {
    if (v === null || v === undefined) return v;
    if (typeof v !== 'object') return v;

    if (seen.has(v)) return '[Circular]';
    seen.add(v);

    if (Array.isArray(v)) {
      return v.map(normalize);
    }

    const keys = Object.keys(v).sort();
    const obj: Record<string, any> = {};
    for (const k of keys) obj[k] = normalize(v[k]);
    return obj;
  };

  return JSON.stringify(normalize(value));
};

const normalizeDateYmd = (raw: unknown): string => {
  if (raw == null) return '';

  if (raw instanceof Date) {
    const t = raw.getTime();
    if (!Number.isFinite(t)) return '';
    const y = raw.getFullYear();
    const m = String(raw.getMonth() + 1).padStart(2, '0');
    const d = String(raw.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  const s = String(raw).trim();
  if (!s || s === 'null' || s === 'undefined') return '';

  // 例: 2025-12-26 / 2025/12/26 / 2025.12.26
  const m1 = s.match(/^(\d{4})[\/\-.](\d{1,2})[\/\-.](\d{1,2})/);
  if (m1) {
    const y = m1[1];
    const m = String(m1[2]).padStart(2, '0');
    const d = String(m1[3]).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  // 例: 2025年12月26日
  const m2 = s.match(/^(\d{4})年(\d{1,2})月(\d{1,2})日/);
  if (m2) {
    const y = m2[1];
    const m = String(m2[2]).padStart(2, '0');
    const d = String(m2[3]).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  // 最後の保険：既に YYYY-MM-DD ならそのまま
  const m3 = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m3) return s;

  return '';
};

const normalizeTeamFiscalYears = (teams: any[] | undefined | null): any[] => {
  if (!Array.isArray(teams)) return [];
  return teams.map((t: any) => {
    const endFiscalYear = (t as any)?.end_fiscal_year ?? (t as any)?.team_fiscal_year ?? null;
    const startFiscalYear = (t as any)?.start_fiscal_year ?? (t as any)?.team_start_fiscal_year ?? null;
    return {
      ...t,
      start_fiscal_year: startFiscalYear,
      end_fiscal_year: endFiscalYear,
    };
  });
};

type WeeklyMasterResponse = Omit<WeeklyMaster, 'attributeBuckets' | 'buckets'> & {
  attributeManage?: AttrFromMaster[];
  attributes?: AttrFromMaster[];
  attrMap?: Record<string, number>;
  buckets?: Record<string, any[]> | Array<{ name: string; items: any[] }>;
  attributeBuckets?: Array<{ name: string; items: any[] }>;
};

const toArray = <T,>(value: T[] | undefined | null): T[] => {
  return Array.isArray(value) ? value : [];
};

const mergeByKey = <T,>(fallbackList: T[], primaryList: T[], keyOf: (item: T) => string): T[] => {
  const merged = new Map<string, T>();

  fallbackList.forEach((item) => {
    const key = keyOf(item);
    if (key) merged.set(key, item);
  });

  primaryList.forEach((item) => {
    const key = keyOf(item);
    if (key) merged.set(key, item);
  });

  return Array.from(merged.values());
};

const attrKeyOf = (attr: any): string => {
  const attrId = toNum(attr?.attr_id ?? attr?.attrId);
  if (typeof attrId === 'number') return `id:${attrId}`;

  const attrName = String(attr?.attr_name ?? attr?.attrName ?? '').normalize('NFKC').trim();
  const deptId = toNum(attr?.department_id ?? attr?.departmentId ?? attr?.dept_id) ?? 0;
  const teamId = toNum(attr?.team_id ?? attr?.teamId) ?? 0;
  const orderNumber = toNum(attr?.order_number ?? attr?.orderNumber) ?? '';

  return `scope:${deptId}:${teamId}:${orderNumber}:${attrName}`;
};

const normalizeAttributeBuckets = (raw: unknown): Array<{ name: string; items: any[] }> => {
  if (Array.isArray(raw)) {
    return raw
      .map((bucket: any) => ({
        name: String(bucket?.name ?? bucket?.label ?? bucket?.attr_name ?? '').trim(),
        items: Array.isArray(bucket?.items) ? bucket.items : [],
      }))
      .filter((bucket) => bucket.name);
  }

  if (raw && typeof raw === 'object') {
    return Object.entries(raw as Record<string, any>).map(([name, items]) => ({
      name,
      items: Array.isArray(items) ? items : [],
    }));
  }

  return [];
};

const bucketItemKeyOf = (item: any): string => {
  const attrId = toNum(item?.attr_id ?? item?.attrId) ?? '';
  const optionId = toNum(item?.option_id ?? item?.optionId ?? item?.id) ?? '';
  const deptId = toNum(item?.department_id ?? item?.departmentId ?? item?.dept_id) ?? 0;
  const teamId = toNum(item?.team_id ?? item?.teamId) ?? 0;
  const value = String(item?.value ?? '').normalize('NFKC').trim();
  const label = String(item?.label ?? item?.name ?? item?.text ?? '').normalize('NFKC').trim();

  return `${attrId}:${optionId}:${deptId}:${teamId}:${value}:${label}`;
};

const mergeAttributeBuckets = (
  fallbackRaw: unknown,
  primaryRaw: unknown
): Array<{ name: string; items: any[] }> => {
  const fallback = normalizeAttributeBuckets(fallbackRaw);
  const primary = normalizeAttributeBuckets(primaryRaw);
  const names = new Set<string>([
    ...fallback.map((bucket) => bucket.name),
    ...primary.map((bucket) => bucket.name),
  ]);

  return Array.from(names).map((name) => {
    const fallbackItems = fallback
      .filter((bucket) => bucket.name === name)
      .flatMap((bucket) => bucket.items);
    const primaryItems = primary
      .filter((bucket) => bucket.name === name)
      .flatMap((bucket) => bucket.items);

    return {
      name,
      items: mergeByKey(fallbackItems, primaryItems, bucketItemKeyOf),
    };
  });
};

const mergeWeeklyMasterResponses = (
  primary: WeeklyMasterResponse,
  fallback?: WeeklyMasterResponse | null
): WeeklyMasterResponse => {
  if (!fallback) return primary;

  const primaryAttrs = toArray((primary as any).attributeManage ?? (primary as any).attributes) as AttrFromMaster[];
  const fallbackAttrs = toArray((fallback as any).attributeManage ?? (fallback as any).attributes) as AttrFromMaster[];
  const attributeManage = mergeByKey(fallbackAttrs, primaryAttrs, attrKeyOf);

  return {
    ...fallback,
    ...primary,
    departments: primary.departments?.length ? primary.departments : fallback.departments ?? [],
    teams: primary.teams?.length ? primary.teams : fallback.teams ?? [],
    attributeManage,
    attributes: attributeManage,
    attrMap: {
      ...((fallback as any).attrMap ?? {}),
      ...((primary as any).attrMap ?? {}),
    },
    attributeBuckets: mergeAttributeBuckets(
      (fallback as any).attributeBuckets ?? (fallback as any).buckets,
      (primary as any).attributeBuckets ?? (primary as any).buckets
    ),
    buckets: (primary as any).buckets ?? (fallback as any).buckets ?? undefined,
    creators: primary.creators?.length ? primary.creators : fallback.creators ?? [],
    approvers: primary.approvers?.length ? primary.approvers : fallback.approvers ?? [],
    approverAMs: primary.approverAMs?.length ? primary.approverAMs : fallback.approverAMs ?? [],
    taskMembers: primary.taskMembers?.length ? primary.taskMembers : fallback.taskMembers ?? [],
  };
};

/* =========================================================
 * OVERWRITE 検索条件キャッシュ（NEW送信後に start/end が空にされても復元できるように）
 * ========================================================= */

type OverwriteSearchCache = {
  savedAt: number;
  division: string;
  team: string;
  startDate: string;
  endDate: string;
};

const buildOverwriteSearchCacheKey = (userKey: string, deptId: number): string => {
  return `${OVERWRITE_SEARCH_CACHE_KEY_PREFIX}:${userKey}:dept:${deptId}`;
};

const readOverwriteSearchCache = (
  userKey: string,
  deptId: number
): OverwriteSearchCache | null => {
  try {
    const key = buildOverwriteSearchCacheKey(userKey, deptId);
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    const json = JSON.parse(raw) as Partial<OverwriteSearchCache> | null;
    if (!json || typeof json !== 'object') return null;

    return {
      savedAt: Number(json.savedAt ?? 0) || 0,
      division: String(json.division ?? ''),
      team: String(json.team ?? ''),
      startDate: normalizeDateYmd(json.startDate ?? ''),
      endDate: normalizeDateYmd(json.endDate ?? ''),
    };
  } catch {
    return null;
  }
};

const writeOverwriteSearchCache = (
  userKey: string,
  deptId: number,
  patch: Partial<Omit<OverwriteSearchCache, 'savedAt'>>
): void => {
  try {
    const base =
      readOverwriteSearchCache(userKey, deptId) ?? ({
        savedAt: 0,
        division: '',
        team: '',
        startDate: '',
        endDate: '',
      } as OverwriteSearchCache);

    const next: OverwriteSearchCache = {
      ...base,
      ...patch,
      savedAt: Date.now(),
      division: String((patch as any)?.division ?? base.division ?? ''),
      team: String((patch as any)?.team ?? base.team ?? ''),
      startDate: normalizeDateYmd((patch as any)?.startDate ?? base.startDate ?? ''),
      endDate: normalizeDateYmd((patch as any)?.endDate ?? base.endDate ?? ''),
    };

    const key = buildOverwriteSearchCacheKey(userKey, deptId);
    localStorage.setItem(key, JSON.stringify(next));
  } catch {
    // localStorage が使えない環境などは黙って無視
  }
};


/**
 * division の表示名から department を解決する。
 *
 * 重要:
 * - Test / test のように大文字小文字だけが違う課が存在する。
 * - そのため、最初から normUpper で比較すると department_id=14 の test が
 *   department_id=11 の Test に誤解決される。
 * - department_id 完全一致 → value 完全一致 → label 完全一致を優先し、
 *   大文字小文字無視の比較は「候補が1件だけ」の場合に限定する。
 */
const findDivisionOptionByStableIdentity = (
  divisionValue: unknown,
  options: any[] | undefined | null
): any | undefined => {
  const rawKey = String(divisionValue ?? '').trim();
  const src = Array.isArray(options) ? options : [];
  if (!rawKey || src.length === 0) return undefined;

  const optionDeptId = (opt: any): string => String(opt?.department_id ?? '').trim();
  const optionValue = (opt: any): string => String(opt?.value ?? '').trim();
  const optionLabel = (opt: any): string => String(opt?.label ?? '').trim();

  // 1) department_id が渡っている場合は最優先。課名重複でも絶対にズレない。
  const byId = src.find((opt: any) => optionDeptId(opt) !== '' && optionDeptId(opt) === rawKey);
  if (byId) return byId;

  // 2) select の value 完全一致。Test と test を別物として扱う。
  const byExactValue = src.find((opt: any) => optionValue(opt) !== '' && optionValue(opt) === rawKey);
  if (byExactValue) return byExactValue;

  // 3) label 完全一致。value が空でも label から正しく引けるようにする。
  const byExactLabel = src.find((opt: any) => optionLabel(opt) !== '' && optionLabel(opt) === rawKey);
  if (byExactLabel) return byExactLabel;

  // 4) 最後の保険。大文字小文字無視は、候補が一意に決まる場合だけ許可する。
  const upperKey = normUpper(rawKey);
  const caseInsensitiveMatches = src.filter((opt: any) => {
    return (
      normUpper(optionValue(opt)) === upperKey ||
      normUpper(optionLabel(opt)) === upperKey ||
      optionDeptId(opt) === rawKey
    );
  });

  if (caseInsensitiveMatches.length === 1) return caseInsensitiveMatches[0];

  const matchedDepartmentIds = new Set(
    caseInsensitiveMatches
      .map((opt: any) => optionDeptId(opt))
      .filter(Boolean)
  );

  if (matchedDepartmentIds.size === 1) return caseInsensitiveMatches[0];

  // Test / test のように複数候補がある場合は、誤った department_id を選ばない。
  return undefined;
};

/* =========================================================
 * コンポーネント
 * ========================================================= */

const SearchArea: React.FC<SearchAreaProps> = (props) => {
  const { palette } = useTheme();
  const isDark = palette.mode === 'dark';
  const {
    activeTab,
    setActiveTab,

    divisionOptions,
    division,
    setDivision,

    team,
    setTeam,
    reportType,
    setReportType,

    reporter,
    setReporter,

    approver3,
    setApprover3,

    startDate,
    setStartDate,
    endDate,
    setEndDate,

    shikimuke,
    setShikimuke,
    lot,
    setLot,

    approverAM,
    setApproverAM,

    enlightSyncEnabled,
    onLoadHistoryData,
    onSearchResults,
    onHistoryLoaded,

    onDynamicFieldsChange,
    onFreeWordsChange,
    onAttributesChange,
    validationErrors,

    onResolvedIdsChange,
    onResolvedTeamDocsetIdChange,
    cognitoAttributes,
    onOverwriteModalClosed,

    onProvideClearNewDates,

    /** 親へ表示値ペイロードをリフトアップ */
    onSearchPayloadChange,
    isLocked,
    lockedByName,
    loginId,
  } = props;

  const isOverwrite = activeTab === 'overwrite';

  // 最新の activeTab を参照するための ref（子の unmount/cleanup から古い closure が呼ばれても事故らない）
  const latestActiveTabRef = useRef<SearchAreaProps['activeTab']>(activeTab);
  latestActiveTabRef.current = activeTab;

  // ユーザ自身の課は profile.division を優先（無ければ profile.department）
  const { profile } = useUserProfile();
  const profileDivision = useMemo(() => {
    const s = String(((profile as any)?.division ?? profile?.department ?? '') || '').trim();
    return s;
  }, [profile]);

  // localStorage のユーザーキー（Cognito email優先、無ければ profile の候補）
  const userCacheKey = useMemo(() => {
    const cognitoEmail = extractEmailFromCognito(cognitoAttributes);
    const profileEmail =
      (profile as any)?.email ??
      (profile as any)?.mail_address ??
      (profile as any)?.login_id ??
      '';
    // loginId もフォールバックに使う
    const email = (cognitoEmail || loginId || profileEmail || '').trim();
    return normalizeEmail(email) || 'anonymous';
  }, [cognitoAttributes, profile, loginId]);

  

  // 最新の resolvedDeptId を参照するための ref
  const latestDeptIdRef = useRef<number | null>(null);

  // department_id から「select の value（opt.value/opt.label）」を逆引きする
  const getDivisionValueByDeptId = useCallback(
    (deptId: any): string | null => {
      const did = toNum(deptId);
      if (typeof did !== 'number') return null;

      const list = (divisionOptions ?? []) as any[];
      const hit = list.find((opt: any) => toNum(opt?.department_id) === did);
      if (!hit) return null;

      const v = String(hit.value ?? hit.label ?? '');
      return v ? v : null;
    },
    [divisionOptions]
  );

  const [ready, setReady] = useState(false);
  const [master, setMaster] = useState<WeeklyMaster | null>(null);
  // taskMembers は「復元完了まで undefined」（[] を渡さない）
  const [taskMembers, setTaskMembers] = useState<any[] | undefined>(undefined);
  const [showPastTeamsNew, setShowPastTeamsNew] = useState(false);
  const [deptTeamLinks, setDeptTeamLinks] = useState<TeamDepartmentLink[]>([]);
  const [resolvedTeamId, setResolvedTeamId] = useState<number | null>(null);
  const [overwritePrefill, setOverwritePrefill] = useState<OverwritePrefill | null>(null);
  const [teamLinksLoading, setTeamLinksLoading] = useState(false);
  const [masterLoading, setMasterLoading] = useState(false);
  const modeControlsLoading = teamLinksLoading || masterLoading || !ready;
  const [error, setError] = useState<string | null>(null);
  const teamLinksRequestSeqRef = useRef(0);
  const masterRequestSeqRef = useRef(0);
  const lastFetchKeyRef = useRef<string | null>(null);
  const userInfoRowsRef = useRef<UserInfoRow[] | null>(null);
  const [userInfoLoaded, setUserInfoLoaded] = useState(false);
  const [userInfoReloadKey, setUserInfoReloadKey] = useState(0);
  const [userInfoVersion, setUserInfoVersion] = useState(0);
  const [scopedDivisionOptions, setScopedDivisionOptions] = useState<OptionsSelectList[]>([]);
  const divisionAutoAppliedRef = useRef(false);
  // ユーザーが課セレクトを手動で変更したか（変更後は自分の課の自動適用で上書きしない）
  const divisionTouchedRef = useRef(false);
  const [isOverwriteModalOpen, setIsOverwriteModalOpen] = useState(false);

  const teamRankById = useMemo(() => {
    const map: Record<string, number> = {};
    (deptTeamLinks ?? []).forEach((link, idx) => {
      const key = String((link as any)?.team_id ?? '').trim();
      if (!key) return;
      if (typeof map[key] === 'number') return;
      map[key] = idx;
    });
    return map;
  }, [deptTeamLinks]);

  const displayMaster = useMemo<WeeklyMaster | null>(() => {
    if (!master) return master;

    const teams = Array.isArray(master.teams) ? master.teams : [];
    if (teams.length <= 1) return master;

    const toFiniteNumber = (v: unknown): number | null => {
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };

    const getTeamIdKey = (row: any): string =>
      String(row?.team_id ?? row?.id ?? '').trim();

    const getOrderNumber = (row: any): number | null =>
      toFiniteNumber((row as any)?.order_number);

    const getTeamIdNumber = (row: any): number | null =>
      toFiniteNumber((row as any)?.team_id ?? (row as any)?.id);

    const getTeamLabel = (row: any): string =>
      String(
        (row as any)?.label ??
          (row as any)?.name ??
          (row as any)?.team_label ??
          (row as any)?.team_name ??
          (row as any)?.value ??
          ''
      ).trim();

    const sortedTeams = [...teams].sort((a: any, b: any) => {
      const keyA = getTeamIdKey(a);
      const keyB = getTeamIdKey(b);

      // マスタの並び順（order_number）を最優先。deptTeamLinks の順は同値/未設定時のフォールバック
      const orderA = getOrderNumber(a);
      const orderB = getOrderNumber(b);
      const hasOrderA = orderA != null;
      const hasOrderB = orderB != null;

      if (hasOrderA || hasOrderB) {
        if (hasOrderA && hasOrderB && orderA !== orderB) return orderA - orderB;
        if (hasOrderA !== hasOrderB) return hasOrderA ? -1 : 1;
      }

      const rankA = keyA ? teamRankById[keyA] : undefined;
      const rankB = keyB ? teamRankById[keyB] : undefined;
      const hasRankA = typeof rankA === 'number';
      const hasRankB = typeof rankB === 'number';

      if (hasRankA || hasRankB) {
        if (hasRankA && hasRankB && rankA !== rankB) return rankA - rankB;
        if (hasRankA !== hasRankB) return hasRankA ? -1 : 1;
      }

      const teamIdA = getTeamIdNumber(a);
      const teamIdB = getTeamIdNumber(b);
      const hasTeamIdA = teamIdA != null;
      const hasTeamIdB = teamIdB != null;

      if (hasTeamIdA || hasTeamIdB) {
        if (hasTeamIdA && hasTeamIdB && teamIdA !== teamIdB) return teamIdA - teamIdB;
        if (hasTeamIdA !== hasTeamIdB) return hasTeamIdA ? -1 : 1;
      }

      const labelCmp = getTeamLabel(a).localeCompare(getTeamLabel(b), 'ja');
      if (labelCmp !== 0) return labelCmp;

      return keyA.localeCompare(keyB, 'ja');
    });

    return {
      ...master,
      teams: sortedTeams,
    };
  }, [master, teamRankById]);

  // OverwriteModal の open 状態を ref にも保持する
  // - モーダル close / unmount 後に返ってくる遅延レスポンスを無視するため
  const overwriteModalOpenRef = useRef<boolean>(false);

  const setOverwriteModalOpen = useCallback((open: boolean) => {
    overwriteModalOpenRef.current = open;
    setIsOverwriteModalOpen(open);
  }, []);

  const handleCreateNewClick = useCallback(() => {
    setOverwriteModalOpen(false);
    setOverwritePrefill(null);
    searchSelectionRef.current = null;
    onSearchPayloadChange?.(null);
    setActiveTab('new');
  }, [onSearchPayloadChange, setActiveTab, setOverwriteModalOpen]);

  const handleOverwriteOldClick = useCallback(() => {
    if (activeTab === 'overwrite') {
      if (!modeControlsLoading) {
        setOverwriteModalOpen(true);
      }
      return;
    }

    setOverwritePrefill(null);
    searchSelectionRef.current = null;
    onSearchPayloadChange?.(null);
    setActiveTab('overwrite');
  }, [activeTab, modeControlsLoading, onSearchPayloadChange, setActiveTab, setOverwriteModalOpen]);

  // NEWタブリセットのための子再マウントキー
  const [newFormResetSeq, setNewFormResetSeq] = useState(0);
  // OVERWRITE -> NEW の戻りで「復元useEffect」を必ず再実行するためのキック
  const [newRestoreKick, setNewRestoreKick] = useState(0);
  // OVERWRITE -> NEW の戻りで、SearchArea1st を1回だけリマウントして初期値を反映させるフラグ
  const pendingNewRemountRef = useRef(false);
  // 「表示値ペイロード」は state ではなく ref に持つ
  const searchSelectionRef = useRef<SearchSelectionPayload | null>(null);
  // NEW下書き（SearchArea1st にだけ渡す“初期値”）
  const [initialNewDraftFields, setInitialNewDraftFields] = useState<LastNewDraftField[] | null>(
    null
  );

  // NEW下書き（freeWords）初期値
  const [initialNewDraftFreeWords, setInitialNewDraftFreeWords] =
    useState<Record<string, string> | null>(null);
  // freeWords の最新参照（stale closure 回避）
  const freeWordsRef = useRef<Record<string, string>>({});
  // freeWords の初回同期（空）で上書きしないためのフラグ
  const skipNextNewFreeWordsSnapshotRef = useRef(false);
  // s日付の初回同期（空）で上書きしないためのフラグ
  const skipNextNewStartDateSnapshotRef = useRef(false);
  const skipNextNewEndDateSnapshotRef = useRef(false);
  // NEW下書きキャッシュの最新（保存用）
  const newDraftCacheRef = useRef<LastNewDraftCache | null>(null);
  // NEW: 前回persistした内容（savedAt除外）を保持して重複保存を防ぐ
  const lastPersistedNewDraftJsonRef = useRef<string>('');
  const pendingNewDraftPersistRef = useRef<{
    deptId: number;
    patch: Partial<LastNewDraftCache>;
  } | null>(null);
  const pendingNewDraftPersistTimerRef = useRef<number | null>(null);
  const pendingNewDraftRetryRef = useRef<{
    deptId: number;
    cache: LastNewDraftCache;
    compareJson: string;
    attempt: number;
  } | null>(null);
  const pendingNewDraftRetryTimerRef = useRef<number | null>(null);
  // ★ NEW: fields snapshot 自体も “同一なら無視” して update depth を止める
  const lastNewFieldsSnapshotJsonRef = useRef<string>('');
  // NEW画面の初回スナップショット（空）で上書きしないためのフラグ
  const skipNextNewSnapshotRef = useRef(false);
  // taskMembers の初回同期（空）で上書きしないためのフラグ
  const skipNextNewTaskMembersSnapshotRef = useRef(false);
  // ユーザーと課の組み合わせごとに1回だけ復元したい。
  // 認証情報の確定前後でキャッシュキーが変わる場合も、同じ課を再確認する。
  const restoredNewDraftKeyRef = useRef<string | null>(null);
  // NEW 復元（hydration）中は「子→親更新」「localStorage保存」を止める
  const newHydratingRef = useRef(false);

  // overwrite -> new へ戻る瞬間、子の snapshot より先にロックする（useEffect だと遅い）
  const prevTabForNewGuardRef = useRef<SearchAreaProps['activeTab']>(activeTab);
  if (activeTab === 'new' && prevTabForNewGuardRef.current === 'overwrite') {
    newHydratingRef.current = true;

    // child が吐く初回 snapshot（空/古い）で NEW 下書きが潰れるのを防ぐ
    skipNextNewSnapshotRef.current = true;
    skipNextNewTaskMembersSnapshotRef.current = true;
    skipNextNewFreeWordsSnapshotRef.current = true;
    skipNextNewStartDateSnapshotRef.current = true;
    skipNextNewEndDateSnapshotRef.current = true;
  }
  prevTabForNewGuardRef.current = activeTab;

  // NEW：master（属性一覧）が後から確定した時に 1回だけ remount するためのシグネチャ
  const appliedNewMasterAttrSigRef = useRef<string | null>(null);

  const lockNewHydration = useCallback(() => {
    newHydratingRef.current = true;
  }, []);

  const unlockNewHydration = useCallback(() => {
    newHydratingRef.current = false;
  }, []);

  // NEW: Footer などで start/end が「空にされた」ことを検知して、fields(開始終了日付)も空に固定する
  const datesClearedRef = useRef(false);

  // NEW: 日付クリアの“直後だけ”旧値復活をブロックするためのタイムスタンプ
  const datesClearedAtRef = useRef<number>(0);

  const prevDatesRef = useRef<{ start: string; end: string }>({ start: '', end: '' });
  const lastNonEmptyDatesRef = useRef<{ start: string; end: string }>({ start: '', end: '' });
  const clearedDatesPersistedRef = useRef(false);

  // taskMembers の最新参照（stale closure 回避）
  const taskMembersRef = useRef<any[] | undefined>(undefined);
  useEffect(() => {
    taskMembersRef.current = taskMembers;
  }, [taskMembers]);

  const clearPendingNewDraftRetry = useCallback(() => {
    pendingNewDraftRetryRef.current = null;
    if (pendingNewDraftRetryTimerRef.current != null) {
      window.clearTimeout(pendingNewDraftRetryTimerRef.current);
      pendingNewDraftRetryTimerRef.current = null;
    }
  }, []);

  const scheduleNewDraftRetry = useCallback(
    (deptId: number, cache: LastNewDraftCache, compareJson: string, attempt = 1) => {
      if (attempt > 2) return;

      if (pendingNewDraftRetryTimerRef.current != null) {
        window.clearTimeout(pendingNewDraftRetryTimerRef.current);
      }

      pendingNewDraftRetryRef.current = {
        deptId,
        cache,
        compareJson,
        attempt,
      };

      const delay = attempt === 1 ? 500 : 1500;
      pendingNewDraftRetryTimerRef.current = window.setTimeout(() => {
        const pending = pendingNewDraftRetryRef.current;
        pendingNewDraftRetryRef.current = null;
        pendingNewDraftRetryTimerRef.current = null;
        if (!pending) return;

        const isCurrentDept = latestDeptIdRef.current === pending.deptId;
        if (
          isCurrentDept &&
          lastPersistedNewDraftJsonRef.current &&
          lastPersistedNewDraftJsonRef.current !== pending.compareJson
        ) {
          return;
        }

        const nextCache: LastNewDraftCache = {
          ...pending.cache,
          savedAt: Date.now(),
        };
        const ok = writeLastNewDraftCache(userCacheKey, pending.deptId, nextCache);
        if (ok) {
          if (isCurrentDept) {
            newDraftCacheRef.current = nextCache;
            lastPersistedNewDraftJsonRef.current = pending.compareJson;
          }
          return;
        }

        scheduleNewDraftRetry(pending.deptId, nextCache, pending.compareJson, pending.attempt + 1);
      }, delay);
    },
    [userCacheKey]
  );

  const writeNewDraftPatchNow = useCallback(
    (
      patch: Partial<LastNewDraftCache>,
      opts?: { force?: boolean; deptId?: number }
    ): boolean => {
      const force = !!opts?.force;
      if (!force && latestActiveTabRef.current !== 'new') return false;
      if (!force && newHydratingRef.current) return false;

      const deptId = opts?.deptId ?? latestDeptIdRef.current;
      if (typeof deptId !== 'number') return false;

      const isCurrentDept = latestDeptIdRef.current === deptId;
      const base =
        (isCurrentDept ? newDraftCacheRef.current : null) ??
        readLastNewDraftCache(userCacheKey, deptId) ??
        { savedAt: Date.now() };
      const next: LastNewDraftCache = {
        ...base,
        ...patch,
        savedAt: Date.now(),
      };

      // savedAt は毎回変わるので比較から除外して重複保存を防ぐ
      const { savedAt: _savedAt, ...compare } = (next as any) ?? {};
      const nextJson = stableStringify(compare);

      // 同一内容なら何もしない（無限ループ/ログ連打/無駄なsetItemを止める）
      if (isCurrentDept && nextJson === lastPersistedNewDraftJsonRef.current) return false;

      if (isCurrentDept) newDraftCacheRef.current = next;
      const ok = writeLastNewDraftCache(userCacheKey, deptId, next);
      if (ok) {
        if (isCurrentDept) lastPersistedNewDraftJsonRef.current = nextJson;
        clearPendingNewDraftRetry();
        return true;
      }

      scheduleNewDraftRetry(deptId, next, nextJson);
      return false;
    },
    [clearPendingNewDraftRetry, scheduleNewDraftRetry, userCacheKey]
  );

  const flushPendingNewDraft = useCallback(
    (force = false): boolean => {
      const pending = pendingNewDraftPersistRef.current;
      if (!pending) return false;

      if (pendingNewDraftPersistTimerRef.current != null) {
        window.clearTimeout(pendingNewDraftPersistTimerRef.current);
        pendingNewDraftPersistTimerRef.current = null;
      }

      pendingNewDraftPersistRef.current = null;
      return writeNewDraftPatchNow(pending.patch, {
        force,
        deptId: pending.deptId,
      });
    },
    [writeNewDraftPatchNow]
  );

  // persistNewDraft は「実際に保存したか」を返す（ログ/二重保存/無限ループ対策）
  const persistNewDraft = useCallback(
    (
      patch: Partial<LastNewDraftCache>,
      opts?: { debounce?: boolean }
    ): boolean => {
      if (latestActiveTabRef.current !== 'new') return false;
      if (newHydratingRef.current) return false;

      const deptId = latestDeptIdRef.current;
      if (typeof deptId !== 'number') return false;

      if (opts?.debounce) {
        const pending = pendingNewDraftPersistRef.current;
        if (pending && pending.deptId !== deptId) {
          flushPendingNewDraft(true);
        }

        const sameDeptPending = pendingNewDraftPersistRef.current;
        pendingNewDraftPersistRef.current = {
          deptId,
          patch: {
            ...(sameDeptPending?.deptId === deptId ? sameDeptPending.patch : {}),
            ...patch,
          },
        };

        if (pendingNewDraftPersistTimerRef.current != null) {
          window.clearTimeout(pendingNewDraftPersistTimerRef.current);
        }

        pendingNewDraftPersistTimerRef.current = window.setTimeout(() => {
          flushPendingNewDraft(true);
        }, 300);

        return true;
      }

      const pending = pendingNewDraftPersistRef.current;
      if (pending) {
        if (pending.deptId === deptId) {
          patch = {
            ...pending.patch,
            ...patch,
          };
          pendingNewDraftPersistRef.current = null;
          if (pendingNewDraftPersistTimerRef.current != null) {
            window.clearTimeout(pendingNewDraftPersistTimerRef.current);
            pendingNewDraftPersistTimerRef.current = null;
          }
        } else {
          flushPendingNewDraft(true);
        }
      }

      return writeNewDraftPatchNow(patch);
    },
    [flushPendingNewDraft, writeNewDraftPatchNow]
  );

  useEffect(() => {
    return () => {
      flushPendingNewDraft(true);
      clearPendingNewDraftRetry();
    };
  }, [clearPendingNewDraftRetry, flushPendingNewDraft]);

  useEffect(() => {
    return () => {
      if (activeTab === 'new') flushPendingNewDraft(true);
    };
  }, [activeTab, flushPendingNewDraft]);

  // NEW / OVERWRITE で division/team を分離して保持（Overwrite操作で NEW の選択を潰さない）
  const lastNewSelectionRef = useRef<{ division: string; team: string }>({ division: '', team: '' });
  const lastOverwriteSelectionRef = useRef<{ division: string; team: string }>(
    {
      division: '',
      team: '',
    }
  );

  // sNEW タブの固定項目（承認者など）を退避して、Overwrite から戻っても潰さない
  const lastNewFixedRef = useRef<{
    reporter: string;
    approver3: string;
    approverAM: string;
    reportType: string;
    shikimuke: string;
    lot: string;
    startDate: string;
    endDate: string;
    taskMembers: any[] | undefined;
  }>({
    reporter: '',
    approver3: '',
    approverAM: '',
    reportType: '',
    shikimuke: '',
    lot: '',
    startDate: '',
    endDate: '',
    taskMembers: undefined,
  });

  // ★OVERWRITE タブの固定項目（主に日付）を退避して、NEW送信後のクリアから守る
  const lastOverwriteFixedRef = useRef<{
    startDate: string;
    endDate: string;
  }>({
    startDate: '',
    endDate: '',
  });

  // dept ごとに 1 回だけ overwrite cache を読み込む
  const loadedOverwriteDeptRef = useRef<number | null>(null);

  useEffect(() => {
    if (activeTab === 'new') {
      lastNewSelectionRef.current = {
        division,
        team: String(team ?? ''),
      };

      // ★ NEW の固定項目も退避（Overwrite に行っても NEW の状態を復元できるようにする）
      lastNewFixedRef.current = {
        reporter: String(reporter ?? ''),
        approver3: String(approver3 ?? ''),
        approverAM: String(approverAM ?? ''),
        reportType: String(reportType ?? ''),
        shikimuke: String(shikimuke ?? ''),
        lot: String(lot ?? ''),
        startDate: String(startDate ?? ''),
        endDate: String(endDate ?? ''),
        taskMembers: taskMembersRef.current,
      };
    } else if (activeTab === 'overwrite') {
      lastOverwriteSelectionRef.current = {
        division,
        team: String(team ?? ''),
      };

      // OVERWRITE 側の start/end を退避（NEW送信後のクリアで消えないように）
      lastOverwriteFixedRef.current = {
        startDate: String(startDate ?? ''),
        endDate: String(endDate ?? ''),
      };

      // OVERWRITE 検索条件を localStorage に保存（dept 単位）
      const deptId = latestDeptIdRef.current;
      if (typeof deptId === 'number') {
        writeOverwriteSearchCache(userCacheKey, deptId, {
          division,
          team: String(team ?? ''),
          startDate: String(startDate ?? ''),
          endDate: String(endDate ?? ''),
        });
      }
    }
  }, [
    activeTab,
    division,
    team,
    reporter,
    approver3,
    approverAM,
    reportType,
    shikimuke,
    lot,
    startDate,
    endDate,
    userCacheKey,
  ]);

    const isEmailLike = (s: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s.trim());
  };

  const extractEmailLoose = (s: string): string => {
    const t = String(s ?? '').trim();
    // "Name <a@b.com>" とか "a@b.com" どっちも拾う
    const m = t.match(/([^\s<>"'()]+@[^\s<>"'()]+\.[^\s<>"'()]+)/);
    return (m?.[1] ?? '').trim();
  };

  const formatName = (last?: string | null, first?: string | null): string => {
    const l = String(last ?? '').trim();
    const f = String(first ?? '').trim();
    if (!l && !f) return '';

    const isAscii =
      /^[A-Za-z\s'_-]+$/.test(l) && /^[A-Za-z\s'_-]+$/.test(f);

    return isAscii ? `${l}, ${f}` : `${l} ${f}`.trim();
  };

  // master / user-info から email -> 氏名 の対応表を作る
  const personNameByEmail = useMemo(() => {
    const map = new Map<string, string>();

    const add = (emailRaw: any, nameRaw: any) => {
      const email = normalizeEmail(extractEmailLoose(String(emailRaw ?? '')));
      const name = String(nameRaw ?? '').trim();

      if (!email) return;
      if (!name) return;
      if (isEmailLike(name)) return; // 氏名側が email なら採用しない

      if (!map.has(email)) map.set(email, name);
    };

    // 1) master の候補（label=氏名, value=email のパターンを拾う）
    const lists: any[][] = [
      (master as any)?.creators ?? [],
      (master as any)?.approvers ?? [],
      (master as any)?.approverAMs ?? [],
      (master as any)?.taskMembers ?? [],
    ];

    lists.forEach((xs) => {
      (Array.isArray(xs) ? xs : []).forEach((opt) => {
        const label = String(opt?.label ?? '').trim();
        const value = String(opt?.value ?? '').trim();

        // value が email / label が氏名
        if (isEmailLike(extractEmailLoose(value)) && label && !isEmailLike(extractEmailLoose(label))) {
          add(value, label);
        }

        // label が email / value が氏名（逆パターンも一応）
        if (isEmailLike(extractEmailLoose(label)) && value && !isEmailLike(extractEmailLoose(value))) {
          add(label, value);
        }
      });
    });

    // 2) /user-info の users（mail_address -> first/last）
    const rows = userInfoRowsRef.current ?? [];
    rows.forEach((u) => {
      const name = formatName(u.last_name, u.first_name);
      add(u.mail_address, name);
    });

    return map;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [master, userInfoLoaded]);

  const normalizePersonText = useCallback(
    (raw: any): string => {
      const s = String(raw ?? '').trim();
      if (!s) return '';

      const email = normalizeEmail(extractEmailLoose(s));
      if (!email) return s;

      // email っぽい時だけ置換を試みる
      if (!isEmailLike(email)) return s;

      return personNameByEmail.get(email) ?? s;
    },
    [personNameByEmail]
  );

  const { onResolvedCreateByChange } = props;

  // （必要なら）ログインユーザー email → 氏名
  const resolvedLoginName = useMemo(() => {
    const cognitoEmail = extractEmailFromCognito(cognitoAttributes);

    const profileEmail =
      (profile as any)?.email ??
      (profile as any)?.mail_address ??
      (profile as any)?.login_id ??
      '';

    const raw = (cognitoEmail || loginId || profileEmail || '').trim();
    if (!raw) return '';

    const maybeName = normalizePersonText(raw);
    return String(maybeName || raw).trim();
  }, [cognitoAttributes, loginId, profile, normalizePersonText]);

  // createBy に使うのは「記入者(reporter)」の最終表示名
  //   - reporter が email なら氏名へ解決
  //   - reporter が空ならログインユーザーへフォールバック
  const resolvedCreateBy = useMemo(() => {
    const r = String(reporter ?? '').trim();
    if (r) return String(normalizePersonText(r) || r).trim();
    return String(resolvedLoginName || '').trim();
  }, [reporter, normalizePersonText, resolvedLoginName]);

  // 親へ通知：常に「記入者由来のcreateBy」を渡す
  useEffect(() => {
    onResolvedCreateByChange?.(resolvedCreateBy);
  }, [onResolvedCreateByChange, resolvedCreateBy]);



  const resolveCurrentDepartmentIdForAttrScope = useCallback((): number | null => {
    const refDeptId = latestDeptIdRef.current;
    if (typeof refDeptId === 'number' && Number.isFinite(refDeptId) && refDeptId > 0) {
      return refDeptId;
    }

    const rawKey = String(division ?? '').trim();
    if (!rawKey) return null;

    const src = (scopedDivisionOptions.length ? scopedDivisionOptions : divisionOptions) as any[];
    const hit = findDivisionOptionByStableIdentity(rawKey, src);

    const deptId = toNum((hit as any)?.department_id);
    return typeof deptId === 'number' && deptId > 0 ? deptId : null;
  }, [division, scopedDivisionOptions, divisionOptions]);

  const resolveCurrentTeamIdForAttrScope = useCallback(
    (payload?: SearchSelectionPayload | null): number | null => {
      if (typeof resolvedTeamId === 'number' && resolvedTeamId > 0) {
        return resolvedTeamId;
      }

      const payloadTeam = (payload as any)?.fixed?.team;
      const payloadTeamHint =
        typeof payloadTeam === 'object'
          ? payloadTeam?.value ?? payloadTeam?.label
          : payloadTeam;

      const hints = [payloadTeamHint, team].filter((v) => String(v ?? '').trim() !== '');

      for (const hint of hints) {
        const direct = toNum(hint);
        if (typeof direct === 'number' && direct > 0) {
          return direct;
        }

        const resolved = resolveTeamIdByHint(hint, deptTeamLinks);
        if (typeof resolved === 'number' && resolved > 0) {
          return resolved;
        }
      }

      return null;
    },
    [resolvedTeamId, team, deptTeamLinks]
  );

  const sanitizeScopedDynamicAttrs = useCallback(
    (
      attrs: Array<{ attr_id: number; attr_name: string; attr_value: string }>,
      payload?: SearchSelectionPayload | null
    ): Array<{ attr_id: number; attr_name: string; attr_value: string }> => {
      const deptId = resolveCurrentDepartmentIdForAttrScope();
      const teamId = resolveCurrentTeamIdForAttrScope(payload);
      const masterAttrs = (((master as any)?.attributeManage ?? (master as any)?.attributes ?? []) as AttrFromMaster[])
        .filter(Boolean);

      const normalizeAttrName = (value: unknown): string => {
        return String(value ?? '')
          .normalize('NFKC')
          .trim()
          .replace(/\s+/g, '')
          .toLowerCase();
      };

      const toScopeId = (value: unknown): number => {
        return toNum(value) ?? 0;
      };

      const knownGlobalCommonAttrIdByName: Record<string, number> = {
        [normalizeAttrName(FIXED_ATTR_NAME_SHIMUKE)]: 5,
        [normalizeAttrName(FIXED_ATTR_NAME_LOT)]: 6,
      };

      const knownAttrNameById: Record<number, string> = {
        5: FIXED_ATTR_NAME_SHIMUKE,
        145: FIXED_ATTR_NAME_SHIMUKE,
        6: FIXED_ATTR_NAME_LOT,
        146: FIXED_ATTR_NAME_LOT,
      };

      const getAttrId = (attr: AttrFromMaster | undefined): number | null => {
        if (!attr) return null;
        const attrId = toNum((attr as any)?.attr_id);
        return typeof attrId === 'number' ? attrId : null;
      };

      const getAttrDeptId = (attr: AttrFromMaster): number => {
        return toScopeId((attr as any)?.department_id ?? (attr as any)?.dept_id);
      };

      const getAttrTeamId = (attr: AttrFromMaster): number => {
        return toScopeId((attr as any)?.team_id);
      };

      const getAttrOrderNumber = (attr: AttrFromMaster): number => {
        const orderNumber = Number((attr as any)?.order_number);
        return Number.isFinite(orderNumber) ? orderNumber : Number.MAX_SAFE_INTEGER;
      };

      const isActiveAttr = (attr: AttrFromMaster): boolean => {
        const rawStatus = (attr as any)?.status;
        if (rawStatus == null || rawStatus === '') return true;

        const status = String(rawStatus).trim().toUpperCase();
        return status === 'Y' || status === '1' || status === 'TRUE';
      };

      const isAllowedAttrForScope = (attr: AttrFromMaster): boolean => {
        if (!isActiveAttr(attr)) return false;

        const attrDeptId = getAttrDeptId(attr);
        const attrTeamId = getAttrTeamId(attr);

        if (deptId == null) {
          return attrDeptId === 0 && attrTeamId === 0;
        }

        if (teamId != null && attrDeptId === deptId && attrTeamId === teamId) {
          return true;
        }

        if (attrDeptId === deptId && attrTeamId === 0) {
          return true;
        }

        if (attrDeptId === 0 && attrTeamId === 0) {
          return true;
        }

        return false;
      };

      const scopeScore = (attr: AttrFromMaster): number => {
        const attrDeptId = getAttrDeptId(attr);
        const attrTeamId = getAttrTeamId(attr);

        if (deptId != null && teamId != null && attrDeptId === deptId && attrTeamId === teamId) {
          return 300;
        }

        if (deptId != null && attrDeptId === deptId && attrTeamId === 0) {
          return 200;
        }

        if (attrDeptId === 0 && attrTeamId === 0) {
          return 100;
        }

        return -1;
      };

      const findMasterAttrById = (attrId: number): AttrFromMaster | undefined => {
        return masterAttrs.find((attr) => getAttrId(attr) === attrId);
      };

      const resolveScopedAttrByName = (attrName: string): AttrFromMaster | undefined => {
        const target = normalizeAttrName(attrName);
        if (!target) return undefined;

        const candidates = masterAttrs
          .filter((attr) => normalizeAttrName((attr as any)?.attr_name) === target)
          .filter(isAllowedAttrForScope)
          .sort((left, right) => {
            const scoreDiff = scopeScore(right) - scopeScore(left);
            if (scoreDiff !== 0) return scoreDiff;

            const orderDiff = getAttrOrderNumber(left) - getAttrOrderNumber(right);
            if (orderDiff !== 0) return orderDiff;

            return (getAttrId(left) ?? Number.MAX_SAFE_INTEGER) - (getAttrId(right) ?? Number.MAX_SAFE_INTEGER);
          });

        return candidates[0];
      };

      const resolveFallbackGlobalCommonAttr = (attrName: string): AttrFromMaster | undefined => {
        const fallbackId = knownGlobalCommonAttrIdByName[normalizeAttrName(attrName)];
        if (!fallbackId) return undefined;

        const found = findMasterAttrById(fallbackId);
        if (found && isAllowedAttrForScope(found)) return found;

        // master に全体共通属性が含まれないレスポンスでも、既知の共通IDだけは安全側で補正する。
        return {
          attr_id: fallbackId,
          attr_name: attrName,
          department_id: 0,
          team_id: 0,
          attr_type: 11,
          multiple_select: 1,
          order_number: Number.MAX_SAFE_INTEGER,
          status: 'Y',
        } as AttrFromMaster;
      };

      const normalizedById = new Map<number, { attr_id: number; attr_name: string; attr_value: string }>();

      (attrs ?? []).forEach((attr) => {
        const attrValue = String(attr?.attr_value ?? '').trim();
        if (!attrValue) return;

        const rawAttrId = toNum((attr as any)?.attr_id);
        const rawMasterAttr = typeof rawAttrId === 'number' ? findMasterAttrById(rawAttrId) : undefined;
        const rawName = String(
          attr?.attr_name ??
            (rawMasterAttr as any)?.attr_name ??
            (rawAttrId != null ? knownAttrNameById[rawAttrId] : '') ??
            ''
        ).trim();
        if (!rawName) return;

        const rawAttrAllowed = rawMasterAttr ? isAllowedAttrForScope(rawMasterAttr) : false;
        const resolvedAttr =
          rawAttrAllowed && rawAttrId != null
            ? rawMasterAttr
            : resolveScopedAttrByName(rawName) ?? resolveFallbackGlobalCommonAttr(rawName);

        const resolvedAttrId = getAttrId(resolvedAttr);
        if (resolvedAttrId == null) return;

        const resolvedAttrName = String((resolvedAttr as any)?.attr_name ?? rawName).trim() || rawName;

        normalizedById.set(resolvedAttrId, {
          attr_id: resolvedAttrId,
          attr_name: resolvedAttrName,
          attr_value: attrValue,
        });
      });

      return Array.from(normalizedById.values());
    },
    [master, resolveCurrentDepartmentIdForAttrScope, resolveCurrentTeamIdForAttrScope]
  );

  const sanitizeScopedSearchPayload = useCallback(
    (payload: SearchSelectionPayload | null): SearchSelectionPayload | null => {
      if (!payload) return payload;

      const dynamicAttrs = sanitizeScopedDynamicAttrs(payload.dynamicAttrs ?? [], payload);
      const attrIdByName = new Map<string, number>();

      dynamicAttrs.forEach((attr) => {
        const key = String(attr.attr_name ?? '')
          .normalize('NFKC')
          .trim()
          .replace(/\s+/g, '')
          .toLowerCase();
        if (key) attrIdByName.set(key, attr.attr_id);
      });

      const entries = (payload.entries ?? []).map((entry) => {
        if ((entry as any)?.isFixed) return entry;

        const key = String((entry as any)?.type ?? '')
          .normalize('NFKC')
          .trim()
          .replace(/\s+/g, '')
          .toLowerCase();
        const scopedAttrId = attrIdByName.get(key);

        return typeof scopedAttrId === 'number'
          ? { ...entry, attr_id: scopedAttrId }
          : entry;
      });

      return {
        ...payload,
        entries,
        dynamicAttrs,
      };
    },
    [sanitizeScopedDynamicAttrs]
  );

  const sanitizeScopedDynamicFieldsForParent = useCallback(
    (fields: any[]): any[] => {
      const fixedNames = new Set<string>([
        'チーム',
        '記入者',
        '連名者',
        '承認-3',
        '承認AM',
        '開始終了日付',
      ]);

      const normalizeFieldAttrName = (value: unknown): string => {
        return String(value ?? '')
          .normalize('NFKC')
          .trim()
          .replace(/\s+/g, '')
          .toLowerCase();
      };

      const sourceFields = Array.isArray(fields) ? fields : [];

      const candidateAttrs = sourceFields
        .filter((field: any) => {
          const type = String(field?.type ?? field?.attr_name ?? '').trim();
          return type && !fixedNames.has(type);
        })
        .map((field: any) => {
          const attrId = toNum(field?.attr_id ?? field?.attrId);
          return {
            attr_id: typeof attrId === 'number' ? attrId : 0,
            attr_name: String(field?.type ?? field?.attr_name ?? field?.label ?? '').trim(),
            attr_value: '__scope_check__',
          };
        })
        .filter((attr) => attr.attr_name);

      const scopedAttrs = sanitizeScopedDynamicAttrs(candidateAttrs);
      const attrIdByName = new Map<string, number>();

      scopedAttrs.forEach((attr) => {
        const key = normalizeFieldAttrName(attr.attr_name);
        if (key) attrIdByName.set(key, attr.attr_id);
      });

      return sourceFields.map((field: any) => {
        const type = String(field?.type ?? field?.attr_name ?? '').trim();
        if (!type || fixedNames.has(type)) return field;

        const key = normalizeFieldAttrName(type);
        const scopedAttrId = attrIdByName.get(key);

        return typeof scopedAttrId === 'number'
          ? { ...field, attr_id: scopedAttrId }
          : field;
      });
    },
    [sanitizeScopedDynamicAttrs]
  );

  // payload を正規化してから ref/親へ
  const handleSearchPayloadChange = useCallback(
    (p: SearchSelectionPayload | null) => {
      if (latestActiveTabRef.current !== 'overwrite') return;

      const normalizedTeam = normalizeTeamInPayload(p, deptTeamLinks);
      const normalized = sanitizeScopedSearchPayload(normalizedTeam);

      searchSelectionRef.current = normalized;
      onSearchPayloadChange?.(normalized ?? null);
    },
    [onSearchPayloadChange, deptTeamLinks, sanitizeScopedSearchPayload]
  );

  // チーム候補(deptTeamLinks)が更新されたら、保持中 payload もチームIDへ再正規化して親に再通知
  useEffect(() => {
    const cur = searchSelectionRef.current;
    if (!cur) return;

    const normalized = sanitizeScopedSearchPayload(
      normalizeTeamInPayload(cur, deptTeamLinks)
    );

    const prevVal =
      typeof (cur as any)?.fixed?.team === 'object'
        ? ((cur as any).fixed.team as any)?.value
        : (cur as any)?.fixed?.team;
    const nextVal =
      typeof (normalized as any)?.fixed?.team === 'object'
        ? ((normalized as any).fixed.team as any)?.value
        : (normalized as any)?.fixed?.team;

    if (prevVal !== nextVal) {
      searchSelectionRef.current = normalized;
      onSearchPayloadChange?.(normalized);
    }
  }, [deptTeamLinks, onSearchPayloadChange, sanitizeScopedSearchPayload]);

  // 承認-3変更コールバック（OverwriteInputSearchArea から呼ばれる）
  const handleApprover3Change = useCallback(
    (v: string | null) => {
      if (latestActiveTabRef.current !== 'overwrite') return;

      const current = (searchSelectionRef.current ?? { fixed: {}, entries: [] }) as any;
      const patched = {
        ...current,
        fixed: {
          ...(current.fixed ?? {}),
          approver3: v ? { value: v, label: v } : null,
        },
      };
      searchSelectionRef.current = patched;
      onSearchPayloadChange?.(patched);
    },
    [onSearchPayloadChange]
  );

  // 承認AM変更コールバック（OverwriteInputSearchArea から呼ばれる）
  const handleApproverAMChange = useCallback(
    (v: string | null) => {
      if (latestActiveTabRef.current !== 'overwrite') return;

      // 重要：overwrite は NEW の state を汚さないため、親state(setApproverAM)は触らない
      const current = (searchSelectionRef.current ?? { fixed: {}, entries: [] }) as any;
      const patched = {
        ...current,
        fixed: {
          ...(current.fixed ?? {}),
          approverAM: v ? { value: v, label: v } : null,
        },
      };
      searchSelectionRef.current = patched;
      onSearchPayloadChange?.(patched);
    },
    [onSearchPayloadChange]
  );

  useEffect(() => {
    if (activeTab !== 'new') return;

    const curStart = normalizeDateYmd(startDate);
    const curEnd = normalizeDateYmd(endDate);

    const prev = prevDatesRef.current;
    const becameEmpty = (prev.start || prev.end) && !curStart && !curEnd;

    // 空 → 非空 になったら解除
    if (curStart || curEnd) {
      datesClearedRef.current = false;
      clearedDatesPersistedRef.current = false;
    }

    if (becameEmpty) {
      // クリア直前の値を保存（復活判定用）
      lastNonEmptyDatesRef.current = { ...prev };

      datesClearedRef.current = true;
      datesClearedAtRef.current = Date.now();

      // 1回だけ fields も空にして persist（復活防止）
      if (!clearedDatesPersistedRef.current) {
        clearedDatesPersistedRef.current = true;

        const base = newDraftCacheRef.current;
        const clearedFields = patchDateRangeFieldInDraftFields(base?.fields, '', '');

        const patch: Partial<LastNewDraftCache> = { startDate: '', endDate: '' };
        if (clearedFields) patch.fields = clearedFields;

        persistNewDraft(patch);
      }
    }

    prevDatesRef.current = { start: curStart, end: curEnd };
  }, [activeTab, startDate, endDate, persistNewDraft]);

  // チーム変更コールバック（OverwriteInputSearchArea から呼ばれる）
  const handleTeamChange = useCallback(
    (value: string | null) => {
      if (latestActiveTabRef.current !== 'overwrite') return;

      // クリア（＝未選択）された場合
      if (value == null || value.trim() === '') {
        // 表示用 state は空文字でOK
        if (setTeam) setTeam('');
        // ID は null に
        setResolvedTeamId(null);

        // payload に「明示クリア」を刻む（fixed.team=null と meta.teamCleared=true も付与）
        const patched = applyTeamSelectionToPayload(
          searchSelectionRef.current,
          null,
          deptTeamLinks
        );
        searchSelectionRef.current = patched;
        onSearchPayloadChange?.(patched);
        return;
      }

      // 通常選択時
      const patched = applyTeamSelectionToPayload(
        searchSelectionRef.current,
        value,
        deptTeamLinks
      );
      const id = resolveTeamIdByHint((patched as any)?.fixed?.team?.value ?? value, deptTeamLinks);
      if (setTeam) setTeam(value);
      setResolvedTeamId(id);

      // fixed.team を value/label で保持しつつ数値化正規化
      searchSelectionRef.current = patched;
      onSearchPayloadChange?.(patched ?? null);
    },
    [setTeam, deptTeamLinks, onSearchPayloadChange]
  );

  // 日付範囲変更コールバック（OverwriteInputSearchArea から呼ばれる）
  const handleDateRangeChange = useCallback(
    ({ from, to }: { from: string; to: string }) => {
      if (latestActiveTabRef.current !== 'overwrite') return;
      if (typeof from !== 'undefined') setStartDate(from);
      if (typeof to !== 'undefined') setEndDate(to);
    },
    [setStartDate, setEndDate]
  );

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // OverwriteModal からの検索結果を安全に親へ流すラッパー
  const handleOverwriteModalSearchResults = useCallback(
    (results: any[]) => {
      // 1) 既に unmount 済み（タブ切替後など）
      if (!mountedRef.current) return;

      // 2) 現在 overwrite タブでない
      if (latestActiveTabRef.current !== 'overwrite') return;

      // 3) モーダルが開いていない（選択後・close 後）
      if (!overwriteModalOpenRef.current) return;

      onSearchResults(results);
    },
    [onSearchResults]
  );

  /* -----------------------------
   * 1) /user-info 取得
   * --------------------------- */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const data = await fetchUserInfoOnce();
        if (cancelled || !mountedRef.current) return;

        userInfoRowsRef.current = Array.isArray(data?.users) ? data.users : [];
        setUserInfoLoaded(true);
      } catch {
        if (cancelled || !mountedRef.current) return;
        userInfoRowsRef.current = [];
        setUserInfoLoaded(true);
      }
      // 取得完了ごとに課の自動選択を再評価させる（再取得時は userInfoLoaded が変わらないため）
      setUserInfoVersion((v) => v + 1);
    })();

    return () => {
      cancelled = true;
    };
  }, [userInfoReloadKey]);

  // ユーザー情報の新規登録が完了したら、描画済みの画面で課の自動選択をやり直す。
  // 登録前に描画された画面は、未登録の /user-info と空プロフィールで先頭の課に寄っているため。
  useEffect(() => {
    const onUserRegistered = () => {
      divisionAutoAppliedRef.current = false;
      setUserInfoReloadKey((k) => k + 1);
    };
    window.addEventListener(USER_REGISTERED_EVENT, onUserRegistered);
    return () => window.removeEventListener(USER_REGISTERED_EVENT, onUserRegistered);
  }, []);

  /* -----------------------------
   * 2) 所属の課を判定して「初期選択」だけ自分の課にする
   *    ※選択肢は絞り込まない（全課を選べるようにする）
   * --------------------------- */
  // 自分の課に対応する選択肢を解決する。
  //   1) user-info の department_id で一致（最優先）
  //   2) profile の課名で一致
  //   3) user-info の課名で一致
  // ログインID（メール）が特定できない場合に、全ユーザーの課を候補にしてしまわないようにする。
  const resolveOwnDivisionOption = useCallback(
    (list: any[]): any | null => {
      if (!Array.isArray(list) || list.length === 0) return null;

      const users = userInfoRowsRef.current ?? [];
      const cognitoEmail = extractEmailFromCognito(cognitoAttributes);

      const profileEmail =
        (profile as any)?.email ?? (profile as any)?.mail_address ?? (profile as any)?.login_id ?? '';

      const targetEmail = (cognitoEmail || loginId || profileEmail || '').trim();

      const filtered = targetEmail
        ? users.filter((u) => normalizeEmail(u.mail_address) === normalizeEmail(targetEmail))
        : [];

      const deptIds = uniq(
        filtered
          .map((u) => {
            const id = toNum((u as any)?.department_id);
            return typeof id === 'number' ? id : null;
          })
          .filter((v): v is number => Number.isFinite(v as number))
      );

      const deptNames = uniq(
        filtered
          .map((u) => String(u?.department ?? '').trim())
          .filter(Boolean)
      );

      let hit =
        deptIds.length > 0
          ? list.find((opt: any) => {
              const id = toNum(opt?.department_id);
              return typeof id === 'number' && deptIds.includes(id);
            })
          : null;

      // Test / test のような大文字小文字違いの課を誤解決しないため、完全一致優先の resolver を使う。
      if (!hit && profileDivision) {
        hit = findDivisionOptionByStableIdentity(profileDivision, list);
      }

      if (!hit && deptNames.length > 0) {
        for (const deptName of deptNames) {
          hit = findDivisionOptionByStableIdentity(deptName, list);
          if (hit) break;
        }
      }

      return hit ?? null;
    },
    [cognitoAttributes, loginId, profile, profileDivision]
  );

  useEffect(() => {
    if (!userInfoLoaded) return;

    // ★ 表示候補は常に全課
    const finalList = (divisionOptions ?? []) as any[];

    const sameList =
      finalList.length === scopedDivisionOptions.length &&
      finalList.every((v, i) => (scopedDivisionOptions as any)[i] === v);

    if (!sameList) setScopedDivisionOptions(finalList);

    // ★ 初期選択だけ「自分の課」に寄せる（1回だけ）
    //   New / Overwrite どちらのタブでも適用する。ユーザーが課を手動で変えた後は上書きしない。
    if (
      !divisionAutoAppliedRef.current &&
      !divisionTouchedRef.current &&
      finalList.length > 0
    ) {
      const hit = resolveOwnDivisionOption(finalList);

      if (hit) {
        const nextVal = String(hit.value ?? hit.label ?? '');
        if (!division || String(division).trim() !== nextVal) setDivision(nextVal);
        divisionAutoAppliedRef.current = true;
      }
    }
  }, [
    userInfoLoaded,
    userInfoVersion,
    divisionOptions,
    division,
    setDivision,
    scopedDivisionOptions,
    resolveOwnDivisionOption,
  ]);

  // 候補に現 division が無ければ、自分の課（解決できなければ先頭）に差し替え
  useEffect(() => {
    // Overwrite では“全課リスト”を基準に存在判定（絞込みによる巻き戻りを防止）
    const baseList = (scopedDivisionOptions.length ? scopedDivisionOptions : divisionOptions) as any[];
    const list = isOverwrite ? (divisionOptions as any[]) : baseList;

    const has = !!findDivisionOptionByStableIdentity(division, list);
    if (!has) {
      divisionAutoAppliedRef.current = false;
      if (list.length > 0) {
        const fallbackOpt = resolveOwnDivisionOption(list) ?? list[0];
        const fallbackVal = String(
          (fallbackOpt as any).value ?? (fallbackOpt as any).label ?? ''
        );
        setDivision(fallbackVal);
      }
    }
  }, [
    scopedDivisionOptions,
    divisionOptions,
    division,
    setDivision,
    isOverwrite,
    resolveOwnDivisionOption,
  ]);

  /* -----------------------------
   * Overwrite モーダル
   * --------------------------- */
  // OVERWRITE OLD モーダルを「閉じる」した時に、課(division)が先頭に戻るのを防ぐ
  const overwriteModalSnapshotRef = useRef<{ division: string; team: string } | null>(null);
  const overwriteModalPrevOpenRef = useRef(false);

  useEffect(() => {
    if (isOverwrite && !modeControlsLoading && !overwritePrefill) {
      setOverwriteModalOpen(true);
    }
  }, [isOverwrite, modeControlsLoading, overwritePrefill, setOverwriteModalOpen]);

  // モーダル open/close の瞬間に「課/チーム」を退避→（未選択クローズなら）復元
  useEffect(() => {
    const wasOpen = overwriteModalPrevOpenRef.current;
    const isOpen = isOverwriteModalOpen;
    overwriteModalPrevOpenRef.current = isOpen;

    // open になった瞬間の課/チームを退避
    if (!wasOpen && isOpen) {
      overwriteModalSnapshotRef.current = {
        division,
        team: String(team ?? ''),
      };
    }

    // close になった瞬間、履歴未選択(= overwritePrefill なし)なら復元
    if (wasOpen && !isOpen) {
      const snap = overwriteModalSnapshotRef.current;
      overwriteModalSnapshotRef.current = null;

      if (!overwritePrefill && snap) {
        if (snap.division && normUpper(division) !== normUpper(snap.division)) {
          setDivision(snap.division);
        }
        if (setTeam && String(team ?? '') !== String(snap.team)) {
          setTeam(String(snap.team));
        }
      }
    }
  }, [isOverwriteModalOpen, overwritePrefill, division, setDivision, setTeam, team]);

  const handleCloseOverwriteModal = useCallback(() => {
    // 先に ref を閉じる（遅延レスポンス対策）
    setOverwriteModalOpen(false);

    // 未選択で閉じた場合は Overwrite 入力を破棄
    setOverwritePrefill(null);
    searchSelectionRef.current = null;
    onSearchPayloadChange?.(null);

    // 親側へ「キャンセル close」として通知
    onOverwriteModalClosed?.({ reason: 'cancel' });
  }, [
    onOverwriteModalClosed,
    onSearchPayloadChange,
    setOverwriteModalOpen,
  ]);


  const makePrefillFromHistory = useCallback((item: HistoryItem): OverwritePrefill => {
    const S = (v: any) => (v == null ? '' : String(v).trim());

    const pick = (...keys: string[]) => {
      for (const k of keys) {
        const v = (item as any)[k];
        if (v != null && S(v) !== '') return S(v);
      }
      for (const k of keys) {
        for (const val of Object.values(item as any)) {
          if (val && typeof val === 'object' && (val as any)[k] != null) {
            const got = (val as any)[k];
            if (S(got) !== '') return S(got);
          }
        }
      }
      return '';
    };

    function pickArray<T = any>(...keys: string[]): T[] {
      for (const k of keys) {
        const v = (item as any)[k];
        if (Array.isArray(v) && v.length) return v as T[];
      }
      for (const k of keys) {
        for (const val of Object.values(item as any)) {
          const vv = (val as any)?.[k];
          if (Array.isArray(vv) && vv.length) return vv as T[];
        }
      }
      return [];
    }

    const baseAttrs = extractWeeklyBaseAttributes(item);

    const reportTypesRaw =
      (pickArray<any>('report_types', 'reportTypes', 'report_type_ids', 'category_ids') as any[]) ||
      [];
    const reportTypesFromStr = splitTokens(pick('report_type', 'reportType', 'category', 'weekly_category'));
    const report_types = (reportTypesRaw.length ? reportTypesRaw : reportTypesFromStr)
      .map((x: any) => (typeof x === 'object' ? x?.value ?? x?.id ?? x?.label ?? '' : x))
      .filter((s: any) => S(s) !== '');

    const membersRaw = pickArray<any>('task_members', 'members', 'taskMembers');
    const members =
      membersRaw.map((m: any) => ({
        lastName: S(m?.last_name ?? m?.lastName ?? m?.family_name ?? ''),
        firstName: S(m?.first_name ?? m?.firstName ?? m?.given_name ?? ''),
        percent: toNum(m?.percent ?? m?.rate),
      })) || [];

    const respondentsRaw = (item as any).respondents ?? (item as any).respondents_1;
    const respondents = respondentsRaw ?? (membersRaw.length > 0 ? membersRaw : null);

    const normalizeHistoryAttrValues = (raw: any) => {
      if (!raw) {
        return [] as Array<{ attr_id?: number | string; attr_name?: string; attr_value?: string }>;
      }

      let arr: any[] = [];

      if (Array.isArray(raw)) {
        arr = raw;
      } else if (typeof raw === 'string') {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed)) {
            arr = parsed;
          }
        } catch {
          arr = [];
        }
      }

      return arr
        .filter((entry) => entry && typeof entry === 'object')
        .map((entry: any) => ({
          attr_id: entry.attr_id ?? entry.attrId ?? entry.attribute_id ?? entry.id,
          attr_name:
            entry.attr_name ??
            entry.attrName ??
            entry.attribute_name ??
            entry.attributeName ??
            entry.name ??
            entry.label,
          attr_value:
            entry.attr_value ??
            entry.attrValue ??
            entry.value ??
            entry.text ??
            '',
        }))
        .filter((entry) => {
          const hasId = entry.attr_id != null && String(entry.attr_id).trim() !== '';
          const hasName = entry.attr_name != null && String(entry.attr_name).trim() !== '';
          const hasValue = entry.attr_value != null && String(entry.attr_value).trim() !== '';
          return (hasId || hasName) && hasValue;
        });
    };

    const pickHistoryAttrValues = () => {
      const candidates = [
        (item as any).attribute_values,
        (item as any).attributes,
        (item as any).attributeValues,
        (item as any).base_attributes,
        (item as any).baseAttributes,
        (item as any).weekly_report_base_attributes,
      ];

      for (const candidate of candidates) {
        const normalized = normalizeHistoryAttrValues(candidate);

        if (normalized.length > 0) {
          return normalized;
        }
      }

      if (baseAttrs.length > 0) {
        return baseAttrs.map((attr) => ({
          attr_id: attr.attr_id,
          attr_name: attr.attr_name,
          attr_value: attr.attr_value,
        }));
      }

      return null;
    };

    const attributeValuesForPrefill = pickHistoryAttrValues();

    const out: OverwritePrefill = {
      team: pick('team_name', 'team_label', 'team_value', 'team'),
      reporter: pick('reporter', 'creator_name', 'create_by_name', 'create_by'),
      approver3: pick('approver3', 'approve3', 'approve_3_name', 'approve_3', 'approver'),
      approverAM: pick('approverAM', 'approveAM', 'approve_am_name', 'approve_am'),
      reportType: pick('reportType', 'report_type', 'category', 'weekly_category'),
      startDate: pick('start_date', 'startDate'),
      endDate: pick('end_date', 'endDate'),
      shikimuke: pick('shikimuke', 'destination'),
      lot: pick('lot'),
      project: pick('project'),
      powertrain: pick('powertrain'),
      taskMembers: members,
      attributes: baseAttrs,

      attribute_values: attributeValuesForPrefill,
      attributeValues: attributeValuesForPrefill,
      base_attributes: attributeValuesForPrefill,
      baseAttributes: attributeValuesForPrefill,
      weekly_report_base_attributes: attributeValuesForPrefill,

      start_date: pick('start_date', 'startDate'),
      end_date: pick('end_date', 'endDate'),
      create_by: pick('create_by', 'creator_name', 'reporter', 'create_by_name'),
      report_types,

      departments: (item as any).department ? [(item as any).department as number] : [],

      approver: pick('approver', 'approve_3', 'approver3'),
      approve_am: pick('approve_am', 'approveAM', 'approverAM'),
      respondents,
      respondents_1: (item as any).respondents_1 ?? null,
      task_members: (item as any).task_members ?? (membersRaw.length > 0 ? membersRaw : null),
      members: (item as any).members ?? null,

      title: pick('title'),
      body: pick('body'),
    };

    return out;
  }, []);

  const handleSelectOverwriteModal = useCallback(
    (item: HistoryItem) => {
      console.log('handleSelectOverwriteModal', {
        wr_id: (item as any)?.wr_id,
        title: (item as any)?.title,
        activeTab: latestActiveTabRef.current,
        isOverwriteModalOpen,
        overwriteModalOpenRef: overwriteModalOpenRef.current,
      });

      // 選択確定時点でモーダルを「論理的に閉じる」
      // 遅れて返ってくる検索結果を無視するため
      setOverwriteModalOpen(false);

      // 注意:
      // raw item.attributes / item.attribute_values には、過去に混入した別チーム属性
      // (例: team_id=36 の attr_id=145/146) が残っている可能性がある。
      // そのため onLoadHistoryData は、下で department/team スコープに正規化した
      // sanitizedHistoryItemForParent を作ってから呼ぶ。

      // 前回の検索/編集中ペイロードをリセットしておく
      searchSelectionRef.current = null;
      onSearchPayloadChange?.(null);

      // 履歴選択 → Overwrite のプリフィル作成
      const pre = makePrefillFromHistory(item);

      type PrefillAttributeValue = {
        attr_id?: number | string;
        attr_name?: string;
        attr_value?: string;
      };

      const normalizePrefillAttrName = (value: unknown): string => {
        return String(value ?? '')
          .normalize('NFKC')
          .trim()
          .replace(/\s+/g, '')
          .toLowerCase();
      };

      const stringifyPrefillValue = (value: unknown): string => {
        if (value == null) {
          return '';
        }

        if (Array.isArray(value)) {
          return value
            .map((itemValue) => stringifyPrefillValue(itemValue))
            .filter(Boolean)
            .join(',');
        }

        if (typeof value === 'object') {
          const objectValue = value as any;

          return String(
            objectValue?.label ??
              objectValue?.value ??
              objectValue?.name ??
              objectValue?.text ??
              ''
          ).trim();
        }

        return String(value).trim();
      };

      const prefillAttrArray = (): PrefillAttributeValue[] => {
        const candidates = [
          pre.attribute_values,
          (pre as any).attributes,
          (pre as any).attributeValues,
          (pre as any).base_attributes,
          (pre as any).baseAttributes,
          (pre as any).weekly_report_base_attributes,
        ];

        for (const raw of candidates) {
          const arr = Array.isArray(raw)
            ? raw
            : typeof raw === 'string'
            ? (() => {
                try {
                  const parsed = JSON.parse(raw);

                  return Array.isArray(parsed) ? parsed : [];
                } catch {
                  return [];
                }
              })()
            : [];

          const normalized = arr
            .filter((entry: any) => {
              return entry && typeof entry === 'object';
            })
            .map((entry: any) => ({
              attr_id: entry.attr_id ?? entry.attrId ?? entry.attribute_id ?? entry.id,
              attr_name:
                entry.attr_name ??
                entry.attrName ??
                entry.attribute_name ??
                entry.attributeName ??
                entry.name ??
                entry.label,
              attr_value: entry.attr_value ?? entry.attrValue ?? entry.value ?? entry.text ?? '',
            }))
            .filter((entry) => {
              const hasId = entry.attr_id != null && String(entry.attr_id).trim() !== '';
              const hasName = entry.attr_name != null && String(entry.attr_name).trim() !== '';
              const hasValue = entry.attr_value != null && String(entry.attr_value).trim() !== '';
              return (hasId || hasName) && hasValue;
            });

          if (normalized.length > 0) {
            return normalized;
          }
        }

        return [];
      };

      const scopedDeptId =
        toNum(
          (item as any)?.department_id ??
            (item as any)?.departmentId ??
            (item as any)?.department ??
            pre.departments?.[0]
        ) ?? null;

      const scopedTeamId =
        toNum((item as any)?.team_id) ??
        resolveTeamIdByHint(
          (item as any)?.team_label ??
            (item as any)?.team_value ??
            (item as any)?.team,
          deptTeamLinks
        ) ??
        null;

      const masterAttrs = (
        (master as any)?.attributeManage ??
        (master as any)?.attributes ??
        []
      ) as AttrFromMaster[];

      const toScopeId = (value: unknown): number => {
        return toNum(value) ?? 0;
      };

      const getAttrId = (attr: AttrFromMaster): number | null => {
        const attrId = toNum((attr as any)?.attr_id);

        return typeof attrId === 'number' ? attrId : null;
      };

      const getAttrDeptId = (attr: AttrFromMaster): number => {
        return toScopeId((attr as any)?.department_id ?? (attr as any)?.dept_id);
      };

      const getAttrTeamId = (attr: AttrFromMaster): number => {
        return toScopeId((attr as any)?.team_id);
      };

      const getAttrOrderNumber = (attr: AttrFromMaster): number => {
        const orderNumber = Number((attr as any)?.order_number);

        return Number.isFinite(orderNumber)
          ? orderNumber
          : Number.MAX_SAFE_INTEGER;
      };

      const isActiveAttr = (attr: AttrFromMaster): boolean => {
        const rawStatus = (attr as any)?.status;

        if (rawStatus == null || rawStatus === '') {
          return true;
        }

        const status = String(rawStatus).trim().toUpperCase();

        return status === 'Y' || status === '1' || status === 'TRUE';
      };

      const isAllowedAttrForScope = (
        attr: AttrFromMaster,
        deptId: number | null,
        teamId: number | null
      ): boolean => {
        if (!isActiveAttr(attr)) {
          return false;
        }

        const attrDeptId = getAttrDeptId(attr);
        const attrTeamId = getAttrTeamId(attr);

        // department/team が解決できない場合は、全体共通のみ許可する。
        if (deptId == null) {
          return attrDeptId === 0 && attrTeamId === 0;
        }

        // 1. 対象チーム専用属性
        if (
          teamId != null &&
          attrDeptId === deptId &&
          attrTeamId === teamId
        ) {
          return true;
        }

        // 2. 対象部署共通属性
        if (attrDeptId === deptId && attrTeamId === 0) {
          return true;
        }

        // 3. 全体共通属性
        if (attrDeptId === 0 && attrTeamId === 0) {
          return true;
        }

        // それ以外、つまり「同じ部署の別チーム属性」は禁止。
        return false;
      };

      const getScopeScore = (
        attr: AttrFromMaster,
        deptId: number | null,
        teamId: number | null
      ): number => {
        if (!isAllowedAttrForScope(attr, deptId, teamId)) {
          return -1;
        }

        const attrDeptId = getAttrDeptId(attr);
        const attrTeamId = getAttrTeamId(attr);

        if (
          deptId != null &&
          teamId != null &&
          attrDeptId === deptId &&
          attrTeamId === teamId
        ) {
          return 300;
        }

        if (deptId != null && attrDeptId === deptId && attrTeamId === 0) {
          return 200;
        }

        if (attrDeptId === 0 && attrTeamId === 0) {
          return 100;
        }

        return -1;
      };

      const findMasterAttrById = (attrId: number): AttrFromMaster | undefined => {
        return masterAttrs.find((attr) => {
          return getAttrId(attr) === attrId;
        });
      };

      const resolveAttrIdFromMap = (attrName: string): number | undefined => {
        const target = normalizePrefillAttrName(attrName);
        const attrMap = ((master as any)?.attrMap ?? {}) as Record<string, unknown>;

        const rawMapped =
          attrMap[attrName] ??
          attrMap[String(attrName).trim()] ??
          attrMap[target];

        const mappedId = toNum(rawMapped);

        if (mappedId == null) {
          return undefined;
        }

        const mappedAttr = findMasterAttrById(mappedId);

        if (!mappedAttr) {
          // attrMap だけが返ってきて masterAttrs 側に同じIDが無いケースを許容する。
          // ただし、masterAttrs に存在する不正スコープIDは下で弾く。
          return mappedId;
        }

        return isAllowedAttrForScope(mappedAttr, scopedDeptId, scopedTeamId)
          ? mappedId
          : undefined;
      };

      const resolveProjectFallbackAttrId = (): number | undefined => {
        const fallbackProjectAttrId = 2;
        const projectAttr = findMasterAttrById(fallbackProjectAttrId);

        if (!projectAttr) {
          return fallbackProjectAttrId;
        }

        return isAllowedAttrForScope(projectAttr, scopedDeptId, scopedTeamId)
          ? fallbackProjectAttrId
          : undefined;
      };

      /**
       * ATTRIBUTE_MANAGE が API レスポンスに含まれないケースの最後の保険。
       * 今回の要件では、team_id=31 に「仕向け/ロット」が無ければ
       * 全体共通の attr_id=5/6 にフォールバックする。
       */
      const resolveGlobalCommonFallbackAttrId = (attrName: string): number | undefined => {
        const target = normalizePrefillAttrName(attrName);

        if (target === normalizePrefillAttrName(FIXED_ATTR_NAME_SHIMUKE)) {
          return 5;
        }

        if (target === normalizePrefillAttrName(FIXED_ATTR_NAME_LOT)) {
          return 6;
        }

        return undefined;
      };

      const knownPrefillAttrNameById: Record<number, string> = {
        5: FIXED_ATTR_NAME_SHIMUKE,
        145: FIXED_ATTR_NAME_SHIMUKE,
        6: FIXED_ATTR_NAME_LOT,
        146: FIXED_ATTR_NAME_LOT,
      };

      const resolvePrefillAttrId = (attrName: string): number | undefined => {
        const target = normalizePrefillAttrName(attrName);

        const candidates = masterAttrs
          .filter((attr) => {
            return normalizePrefillAttrName((attr as any)?.attr_name) === target;
          })
          .filter((attr) => {
            return isAllowedAttrForScope(attr, scopedDeptId, scopedTeamId);
          })
          .sort((left, right) => {
            const scoreDiff =
              getScopeScore(right, scopedDeptId, scopedTeamId) -
              getScopeScore(left, scopedDeptId, scopedTeamId);

            if (scoreDiff !== 0) {
              return scoreDiff;
            }

            const orderDiff =
              getAttrOrderNumber(left) - getAttrOrderNumber(right);

            if (orderDiff !== 0) {
              return orderDiff;
            }

            return (
              (getAttrId(left) ?? Number.MAX_SAFE_INTEGER) -
              (getAttrId(right) ?? Number.MAX_SAFE_INTEGER)
            );
          });

        const candidateId = getAttrId(candidates[0]);

        if (candidateId != null) {
          return candidateId;
        }

        const mappedId = resolveAttrIdFromMap(attrName);

        if (mappedId != null) {
          return mappedId;
        }

        if (target === normalizePrefillAttrName(FIXED_ATTR_NAME_PROJECT)) {
          return resolveProjectFallbackAttrId();
        }

        const globalCommonFallbackId = resolveGlobalCommonFallbackAttrId(attrName);

        if (globalCommonFallbackId != null) {
          return globalCommonFallbackId;
        }

        return undefined;
      };

      /**
       * pre.attribute_values 全体を現在の department/team スコープに正規化する。
       *
       * 重要:
       * 既存データが { attr_id: 145, attr_value: '共通' } のように
       * attr_name を持たない場合でも、ATTRIBUTE_MANAGE から 145=仕向け を逆引きし、
       * 現在スコープで有効な 5=仕向け に差し替える。
       */
      const sanitizeScopedPrefillAttributeValues = () => {
        const current = prefillAttrArray();
        const next: PrefillAttributeValue[] = [];
        const seen = new Set<string>();

        const push = (attrId: number, attrName: string, attrValue: string) => {
          const canonicalName = String(attrName ?? '').trim();
          const canonicalValue = String(attrValue ?? '').trim();

          if (!Number.isFinite(attrId) || !canonicalName || !canonicalValue) {
            return;
          }

          const key = `${attrId}__${canonicalValue}`;

          if (seen.has(key)) {
            return;
          }

          seen.add(key);
          next.push({
            attr_id: attrId,
            attr_name: canonicalName,
            attr_value: canonicalValue,
          });
        };

        current.forEach((entry) => {
          const entryAttrId = toNum(entry.attr_id);
          const entryMeta = entryAttrId != null
            ? findMasterAttrById(entryAttrId)
            : undefined;

          const entryName = String(
            entry.attr_name ??
              entryMeta?.attr_name ??
              (entryAttrId != null ? knownPrefillAttrNameById[entryAttrId] : '') ??
              ''
          ).trim();
          const entryValue = stringifyPrefillValue(entry.attr_value);

          if (!entryValue) {
            return;
          }

          if (entryName) {
            const scopedAttrId = resolvePrefillAttrId(entryName);

            if (scopedAttrId != null) {
              push(scopedAttrId, entryName, entryValue);
              return;
            }
          }

          if (
            entryAttrId != null &&
            entryMeta &&
            isAllowedAttrForScope(entryMeta, scopedDeptId, scopedTeamId)
          ) {
            push(entryAttrId, entryMeta.attr_name, entryValue);
            return;
          }

          // master がまだ対象 department/team で再取得されていないタイミングでは、
          // 工順/現象などのチーム専用属性を解決できないことがある。
          // ここで捨てると OverwriteInputSearchArea へ DB 値が届かず空表示になるため、
          // 元の attr_id / attr_name を一旦保持し、子側で master ロード後に再解決させる。
          if (entryAttrId != null && entryName) {
            push(entryAttrId, entryName, entryValue);
          }
        });

        pre.attribute_values = next;
        (pre as any).attributeValues = next;
        (pre as any).base_attributes = next;
        (pre as any).baseAttributes = next;
        (pre as any).weekly_report_base_attributes = next;
      };

      const resolvePrefillAttrValue = (
        attrName: string,
        rawValue: unknown
      ): string => {
        const directValue = stringifyPrefillValue(rawValue);

        if (directValue) {
          return directValue;
        }

        const target = normalizePrefillAttrName(attrName);

        const existing = prefillAttrArray().find((entry) => {
          return normalizePrefillAttrName(entry.attr_name) === target;
        });

        return stringifyPrefillValue(existing?.attr_value);
      };

      const ensureScopedPrefillAttributeValue = (
        attrName: string,
        rawValue: unknown
      ) => {
        const attrNameKey = normalizePrefillAttrName(attrName);
        const attrValue = resolvePrefillAttrValue(attrName, rawValue);
        const attrId = resolvePrefillAttrId(attrName);

        const current = prefillAttrArray();

        const next = current.filter((entry) => {
          const existingNameKey = normalizePrefillAttrName(entry.attr_name);
          const existingAttrId = toNum(entry.attr_id);

          if (existingNameKey === attrNameKey) {
            return false;
          }

          if (
            attrId != null &&
            existingAttrId != null &&
            existingAttrId === attrId
          ) {
            return false;
          }

          return true;
        });

        // 値が無ければ、同名の古い attribute_values だけ除去して終わる。
        if (!attrValue) {
          pre.attribute_values = next;
          return;
        }

        // スコープ内の attr_id を解決できない場合は、誤った attr_id を送らない。
        // 例: department=1/team=31 で attr_id=145/team=36 を送る事故を防ぐ。
        if (attrId == null) {
          pre.attribute_values = next;
          return;
        }

        next.push({
          attr_id: attrId,
          attr_name: attrName,
          attr_value: attrValue,
        });

        pre.attribute_values = next;
      };

      sanitizeScopedPrefillAttributeValues();

      ensureScopedPrefillAttributeValue(FIXED_ATTR_NAME_PROJECT, pre.project);
      ensureScopedPrefillAttributeValue(
        FIXED_ATTR_NAME_SHIMUKE,
        pre.shikimuke ?? (pre as any).destination
      );
      ensureScopedPrefillAttributeValue(FIXED_ATTR_NAME_LOT, pre.lot);
      ensureScopedPrefillAttributeValue(FIXED_ATTR_NAME_POWERTRAIN, pre.powertrain);

      const sanitizedAttributeValuesForParent = prefillAttrArray()
        .map((entry) => {
          const attrId = toNum(entry.attr_id);
          const attrName = String(entry.attr_name ?? '').trim();
          const attrValue = stringifyPrefillValue(entry.attr_value);

          if (attrId == null || !attrName || !attrValue) {
            return null;
          }

          return {
            wr_id: (item as any)?.wr_id,
            attr_id: attrId,
            attr_name: attrName,
            attr_value: attrValue,
          };
        })
        .filter(Boolean) as Array<{
          wr_id?: number | string;
          attr_id: number;
          attr_name: string;
          attr_value: string;
        }>;

      // 親が raw item.attributes を保存 payload の材料にしても、
      // 145/146 が復活しないように attributes 系プロパティも差し替える。
      (pre as any).attributes = sanitizedAttributeValuesForParent.map((attr) => ({ ...attr }));
      (pre as any).attribute_values = sanitizedAttributeValuesForParent.map((attr) => ({
        attr_id: attr.attr_id,
        attr_name: attr.attr_name,
        attr_value: attr.attr_value,
      }));

      const sanitizedHistoryItemForParent = {
        ...(item as any),
        attributes: sanitizedAttributeValuesForParent.map((attr) => ({ ...attr })),
        attribute_values: sanitizedAttributeValuesForParent.map((attr) => ({
          attr_id: attr.attr_id,
          attr_name: attr.attr_name,
          attr_value: attr.attr_value,
        })),
        attributeValues: sanitizedAttributeValuesForParent.map((attr) => ({
          attr_id: attr.attr_id,
          attr_name: attr.attr_name,
          attr_value: attr.attr_value,
        })),
        base_attributes: sanitizedAttributeValuesForParent.map((attr) => ({ ...attr })),
        baseAttributes: sanitizedAttributeValuesForParent.map((attr) => ({ ...attr })),
        weekly_report_base_attributes: sanitizedAttributeValuesForParent.map((attr) => ({ ...attr })),
      };

      if (isOverwrite) {
        onLoadHistoryData(sanitizedHistoryItemForParent as HistoryItem);
      }

      // このプリフィルがどの週報かを明示
      (pre as any).wr_id = (item as any).wr_id;

      // 子コンポーネントには team_id を優先して渡す。
      // item.team が表示名で渡るケースでも、team_id=31 を確実に使わせるため。
      const nextTeamValue = scopedTeamId != null
        ? String(scopedTeamId)
        : (item as any)?.team != null
        ? String((item as any).team)
        : '';

      (pre as any).team = nextTeamValue;

      setOverwritePrefill(pre);

      // Overwrite 選択内容を親の team / division にも反映し、IDも同期
      if (setTeam) {
        setTeam(nextTeamValue);
      }

      // Division はセレクトの value（opt.value/opt.label）で管理しているため、
      // department_id から「value（文字列）」へ逆引きしてセットする
      if (setDivision) {
        const nextDivision = getDivisionValueByDeptId((item as any)?.department);

        if (nextDivision) {
          setDivision(nextDivision);
        }
      }

      setResolvedTeamId(typeof scopedTeamId === 'number' ? scopedTeamId : null);

      setIsOverwriteModalOpen(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isOverwrite,
      onLoadHistoryData,
      onOverwriteModalClosed,
      onSearchPayloadChange,
      makePrefillFromHistory,
      setTeam,
      setDivision,
      deptTeamLinks,
      master,
      getDivisionValueByDeptId,
    ]
  );

  /* -----------------------------
   * 3) division → department_id 解決
   * --------------------------- */
  const currentDivisionOption = useMemo(() => {
    const src = (scopedDivisionOptions.length ? scopedDivisionOptions : divisionOptions) as any[];

    return findDivisionOptionByStableIdentity(division, src);
  }, [division, scopedDivisionOptions, divisionOptions]);

  const resolvedDeptId = useMemo<number | null>(() => {
    const id = Number((currentDivisionOption as any)?.department_id);
    return Number.isFinite(id) ? id : null;
  }, [currentDivisionOption]);

  const overwritePrefillDepartmentId = useMemo<number | null>(() => {
    const candidate = overwritePrefill?.departments?.[0];
    const id = Number(candidate);
    return Number.isFinite(id) && id > 0 ? id : null;
  }, [overwritePrefill]);

  const overwriteDepartmentId = resolvedDeptId ?? overwritePrefillDepartmentId;

  useEffect(() => {
    latestDeptIdRef.current = typeof resolvedDeptId === 'number' ? resolvedDeptId : null;
  }, [resolvedDeptId]);

  /* -----------------------------
   * OVERWRITE: 検索条件キャッシュの読み込み（dept単位で1回）
   * --------------------------- */
  useEffect(() => {
    if (typeof resolvedDeptId !== 'number') return;

    if (loadedOverwriteDeptRef.current === resolvedDeptId) return;
    loadedOverwriteDeptRef.current = resolvedDeptId;

    const cached = readOverwriteSearchCache(userCacheKey, resolvedDeptId);
    if (!cached) return;

    lastOverwriteSelectionRef.current = {
      division: cached.division,
      team: cached.team,
    };

    lastOverwriteFixedRef.current = {
      startDate: cached.startDate,
      endDate: cached.endDate,
    };
  }, [resolvedDeptId, userCacheKey]);

  // ★ NEW: dept 変更時は taskMembers をいったん undefined に戻す（復元完了まで undefined）
  const prevResolvedDeptIdForNewRef = useRef<number | null>(null);
  useEffect(() => {
    if (activeTab !== 'new') return;

    const curr = typeof resolvedDeptId === 'number' ? resolvedDeptId : null;
    const prev = prevResolvedDeptIdForNewRef.current;

    if (curr !== null && curr !== prev) {
      setTaskMembers(undefined);
      taskMembersRef.current = undefined;
      skipNextNewTaskMembersSnapshotRef.current = true;

      // ★ 修正：deptが変わったら NEWの初期値（fields/freeWords）も必ずクリア（前deptの値が残る事故防止）
      setInitialNewDraftFields(null);
      setInitialNewDraftFreeWords(null);
      freeWordsRef.current = {};
      newDraftCacheRef.current = null;
      restoredNewDraftKeyRef.current = null;

      // ★ NEW: dept を跨いだ比較にならないようにリセット
      lastPersistedNewDraftJsonRef.current = '';

      // ★ NEW: fields snapshot の比較もリセット
      lastNewFieldsSnapshotJsonRef.current = '';

      // ★ master確定後 remount の判定もリセット
      appliedNewMasterAttrSigRef.current = null;
    }

    prevResolvedDeptIdForNewRef.current = curr;
  }, [activeTab, resolvedDeptId]);

  /* -----------------------------
   * NEW: 前回入力値の復元（開始/終了も復元する）
   * --------------------------- */
  useEffect(() => {
    if (activeTab !== 'new') return;
    if (typeof resolvedDeptId !== 'number') return;

    // ユーザーと課の組み合わせごとに1回だけ復元（画面操作で上書きしない）
    const restoreKey = `${userCacheKey}:${resolvedDeptId}`;
    if (restoredNewDraftKeyRef.current === restoreKey) return;

    const cached = readLastNewDraftCache(userCacheKey, resolvedDeptId);
    if (!cached) {
      // 復元が「無い」ことが確定したので、taskMembers を空配列に確定（未確定の undefined のままにしない）
      if (typeof taskMembers === 'undefined') {
        setTaskMembers([]);
      }

      // freeWords も復元が無いので空で確定しておく
      freeWordsRef.current = {};
      setInitialNewDraftFreeWords({});

      // fields も前deptの残骸があれば消す
      setInitialNewDraftFields(null);

      // NEW: 比較の基準をクリア（別deptの残りを引かない）
      lastPersistedNewDraftJsonRef.current = '';

      // NEW: fields snapshot 比較もクリア
      lastNewFieldsSnapshotJsonRef.current = '';

      // キャッシュ無しでも remount 予約がある場合は 1 回 remount する
      //（NEW→OVERWRITE→NEW で子がクリアされたまま再構築されないケースの対策）
      if (pendingNewRemountRef.current) {
        pendingNewRemountRef.current = false;

        lockNewHydration();
        skipNextNewSnapshotRef.current = true;
        skipNextNewTaskMembersSnapshotRef.current = true;
        skipNextNewFreeWordsSnapshotRef.current = true;
        // 日付も初回空で潰されないように
        skipNextNewStartDateSnapshotRef.current = true;
        skipNextNewEndDateSnapshotRef.current = true;

        setNewFormResetSeq((s) => s + 1);
      }

      restoredNewDraftKeyRef.current = restoreKey;
      return;
    }

    newDraftCacheRef.current = cached;

    // 既存キャッシュを「前回persist済み」としてセット（savedAt除外）
    const { savedAt: _savedAt, ...compareCached } = (cached as any) ?? {};
    lastPersistedNewDraftJsonRef.current = stableStringify(compareCached);

    const hasFields = Array.isArray(cached.fields) && cached.fields.length > 0;
    const hasTaskMembers = Array.isArray(cached.taskMembers) && cached.taskMembers.length > 0;

    // “今が空なら” 前回値を入れる（ユーザーの現入力は潰さない）
    // 修正B：上の if 群をやめて「差分なら復元」に置き換える（Overwriteで入った値も確実に戻す）
    const applyIfDifferent = (setter: (v: string) => void, current: any, nextRaw: any) => {
      const next = String(nextRaw ?? '').trim();
      if (!next) return;

      const cur = String(current ?? '').trim();
      if (cur !== next) setter(next);
    };

    if (setTeam) applyIfDifferent(setTeam, team, cached.team);
    applyIfDifferent(setReporter, reporter, cached.reporter);
    applyIfDifferent(setApprover3, approver3, cached.approver3);
    applyIfDifferent(setApproverAM, approverAM, cached.approverAM);
    if (setReportType) applyIfDifferent(setReportType, reportType, cached.reportType);
    applyIfDifferent(setShikimuke, shikimuke, cached.shikimuke);
    applyIfDifferent(setLot, lot, cached.lot);

    // 日付復元（top-level が無い場合は fields の「開始終了日付」から拾う）
    const dateField = Array.isArray(cached.fields)
      ? cached.fields.find((f: any) => String(f?.type ?? '').trim() === '開始終了日付')
      : null;

    const startFromFieldsRaw = String((dateField as any)?.value?.from ?? '').trim();
    const endFromFieldsRaw = String((dateField as any)?.value?.to ?? '').trim();
    const startTop = normalizeDateYmd(cached.startDate);
    const endTop = normalizeDateYmd(cached.endDate);

    // Footer が dates をクリアしたケースを尊重：top-level が空なら fields から復活させない
    const clearedInCache = startTop === '' && endTop === '';

    const startFromCache = clearedInCache ? '' : (startTop || normalizeDateYmd(startFromFieldsRaw));
    const endFromCache = clearedInCache ? '' : (endTop || normalizeDateYmd(endFromFieldsRaw));

    // キャッシュが「クリア」なら、Overwriteで上書きされた state も必ず空に戻す
    if (clearedInCache) {
      datesClearedRef.current = true;
      clearedDatesPersistedRef.current = true;

      if (normalizeDateYmd(startDate) !== '') setStartDate('');
      if (normalizeDateYmd(endDate) !== '') setEndDate('');
    } else {
      // “今が空なら” ではなく “違うなら” 復元（Overwriteで入った old を確実に戻す）
      if (startFromCache && String(startDate ?? '') !== startFromCache) setStartDate(startFromCache);
      if (endFromCache && String(endDate ?? '') !== endFromCache) setEndDate(endFromCache);
    }

    // taskMembers も復元（復元前は undefined / または空なら）
    if (
      hasTaskMembers &&
      (typeof taskMembers === 'undefined' || (Array.isArray(taskMembers) && taskMembers.length === 0))
    ) {
      setTaskMembers(cached.taskMembers as any[]);
      // 初回の空同期で上書きしない（taskMembers用）
      skipNextNewTaskMembersSnapshotRef.current = true;
    }

    // taskMembers がキャッシュに無い場合でも、復元完了として空配列に確定しておく
    if (!hasTaskMembers && typeof taskMembers === 'undefined') {
      setTaskMembers([]);
    }

    // 子（SearchArea1st）の動的項目復元用
    if (hasFields) {
      // fields の開始終了日付も top-level と同期して復元（old 表示を防ぐ）
      const reconciled = patchDateRangeFieldInDraftFields(
        cached.fields as LastNewDraftField[],
        startFromCache,
        endFromCache
      );
      const restoredFields = (reconciled ?? cached.fields) as LastNewDraftField[];
      setInitialNewDraftFields(restoredFields);

      // リロード／画面復帰時は hydration 中の子スナップショットを抑止しているため、
      // 復元値をここで親にも同期し、発行前の公開範囲判定で週報分類を参照できるようにする。
      onDynamicFieldsChange?.(sanitizeScopedDynamicFieldsForParent(restoredFields));
    } else {
      // fields が無いなら前の残骸は消す
      setInitialNewDraftFields(null);
    }

    const hasFreeWords =
      cached.freeWords && Object.values(cached.freeWords).some((v) => String(v ?? '').trim() !== '');

    if (hasFreeWords) {
      const fw = sanitizeFreeWords(cached.freeWords);
      freeWordsRef.current = fw;
      setInitialNewDraftFreeWords(fw);

      // 初回の空スナップショットで上書きしない（freeWords用）
      skipNextNewFreeWordsSnapshotRef.current = true;

      // 親にも復元値を通知（検索条件などで使うなら同期が必要）
      onFreeWordsChange?.(fw);
    }

    // freeWords がキャッシュに無い場合でも、復元完了として空で確定しておく
    if (!hasFreeWords && initialNewDraftFreeWords == null) {
      freeWordsRef.current = {};
      setInitialNewDraftFreeWords({});
    }

    // 復元した初期値(initialNewDraftFreeWords/fields)を SearchArea1st に確実に反映させるため、
    // このタイミングだけ NEW 側の子を remount する
    //（子が props 更新で内部 state を再構築しない実装でも確実に表示される）
    const shouldRemount = pendingNewRemountRef.current || hasFields || hasTaskMembers || hasFreeWords;

    if (shouldRemount) {
      pendingNewRemountRef.current = false;

      // remount直前に hydrationロック＋初回空スナップショットの上書き抑止を必ず立てる
      lockNewHydration();
      skipNextNewSnapshotRef.current = true;
      skipNextNewTaskMembersSnapshotRef.current = true;
      skipNextNewFreeWordsSnapshotRef.current = true;
      // 日付も同様に抑止
      skipNextNewStartDateSnapshotRef.current = true;
      skipNextNewEndDateSnapshotRef.current = true;

      setNewFormResetSeq((s) => s + 1);
    } else {
      // 保険：予約が残らないように落とす
      pendingNewRemountRef.current = false;
    }

    restoredNewDraftKeyRef.current = restoreKey;
  }, [
    activeTab,
    resolvedDeptId,
    userCacheKey,
    team,
    setTeam,
    reporter,
    setReporter,
    approver3,
    setApprover3,
    approverAM,
    setApproverAM,
    reportType,
    setReportType,
    shikimuke,
    setShikimuke,
    lot,
    setLot,
    taskMembers,
    initialNewDraftFreeWords,
    onDynamicFieldsChange,
    onFreeWordsChange,
    newRestoreKick,
    lockNewHydration,
    sanitizeScopedDynamicFieldsForParent,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
  ]);

  /* -----------------------------
   * 4) TEAM_MANAGE 取得
   * --------------------------- */
  useEffect(() => {
    const requestSeq = ++teamLinksRequestSeqRef.current;

    if (typeof resolvedDeptId !== 'number') {
      setTeamLinksLoading(false);
      if (deptTeamLinks.length) setDeptTeamLinks([]);
      if (resolvedTeamId !== null) setResolvedTeamId(null);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setTeamLinksLoading(true);
        setError(null);

        const linksRaw = await fetchByDeptWithCache(resolvedDeptId, false);
        if (cancelled || !mountedRef.current || teamLinksRequestSeqRef.current !== requestSeq) return;

        const links = [...(Array.isArray(linksRaw) ? linksRaw : [])]
          .filter((l) => Number(l?.department_id) === resolvedDeptId)
          .sort((a, b) => Number(a?.order_number ?? 999999) - Number(b?.order_number ?? 999999));

        const same =
          links.length === deptTeamLinks.length &&
          links.every((v, i) => {
            const w = deptTeamLinks[i];
            return (
              w &&
              w.team_id === v.team_id &&
              w.department_id === v.department_id &&
              w.team_label === v.team_label &&
              w.team_value === v.team_value
            );
          });

        if (!same) setDeptTeamLinks(links);
      } catch (err: any) {
        if (cancelled || !mountedRef.current || teamLinksRequestSeqRef.current !== requestSeq) return;
        setError(err?.message ?? String(err));
        if (deptTeamLinks.length) setDeptTeamLinks([]);
      } finally {
        if (mountedRef.current && teamLinksRequestSeqRef.current === requestSeq) {
          setTeamLinksLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedDeptId]);

  /* -----------------------------
   * NEW のチーム変更を親がフックしてキャッシュ保存
   *   - SearchArea1st には setTeam として渡す
   * --------------------------- */
  const handleNewTeamChange = useCallback(
    (value: string) => {
      if (latestActiveTabRef.current !== 'new') return;
      if (newHydratingRef.current) return; // ★追加

      if (setTeam) setTeam(value);

      const deptId = latestDeptIdRef.current;

      // links 未ロード時に team_id を null で上書きしない
      const idRaw = resolveTeamIdByHint(value, deptTeamLinks);
      const id =
        typeof idRaw === 'number'
          ? idRaw
          : deptTeamLinks.length === 0
          ? typeof resolvedTeamId === 'number'
            ? resolvedTeamId
            : toNum(newDraftCacheRef.current?.team_id) ?? null
          : null;

      setResolvedTeamId(id);

      if (typeof deptId === 'number' && !isOverwrite) {
        writeLastNewTeamCache(userCacheKey, deptId, {
          team: value,
          team_id: typeof id === 'number' ? id : null,
          savedAt: Date.now(),
        });

        // team_id は “解決できた時だけ” 更新（nullで潰さない）
        const patch: Partial<LastNewDraftCache> = { team: value };
        if (typeof id === 'number') patch.team_id = id;
        persistNewDraft(patch);
      }
    },
    [setTeam, deptTeamLinks, userCacheKey, isOverwrite, persistNewDraft, resolvedTeamId]
  );

  const handleNewReporterChange = useCallback(
    (v: string) => {
      if (latestActiveTabRef.current !== 'new') return;
      setReporter(v);
      persistNewDraft({ reporter: v });
    },
    [setReporter, persistNewDraft]
  );

  const handleNewApprover3Change = useCallback(
    (v: string) => {
      if (latestActiveTabRef.current !== 'new') return;
      setApprover3(v);
      persistNewDraft({ approver3: v });
    },
    [setApprover3, persistNewDraft]
  );

  const handleNewApproverAMChange = useCallback(
    (v: string) => {
      if (latestActiveTabRef.current !== 'new') return;
      setApproverAM(v);
      persistNewDraft({ approverAM: v });
    },
    [setApproverAM, persistNewDraft]
  );

  const handleNewReportTypeChange = useCallback(
    (v: string) => {
      if (latestActiveTabRef.current !== 'new') return;
      setReportType?.(v);
      persistNewDraft({ reportType: v });
    },
    [setReportType, persistNewDraft]
  );

  const handleNewShikimukeChange = useCallback(
    (v: string) => {
      if (latestActiveTabRef.current !== 'new') return;
      setShikimuke(v);
      persistNewDraft({ shikimuke: v });
    },
    [setShikimuke, persistNewDraft]
  );

  const handleNewLotChange = useCallback(
    (v: string) => {
      if (latestActiveTabRef.current !== 'new') return;
      setLot(v);
      persistNewDraft({ lot: v });
    },
    [setLot, persistNewDraft]
  );

  /* -----------------------------
   * NEW の start/end 変更を親がフックして localStorage 保存
   * --------------------------- */
  const handleNewStartDateChange = useCallback(
    (v: string) => {
      if (latestActiveTabRef.current !== 'new') return;

      const vv = normalizeDateYmd(v);
      const cur = normalizeDateYmd(startDate);

      const isWipingNonEmpty = vv === '' && cur !== '';

      // 復元/リマウント直後に子が吐く空値で state も潰さない（ただし非空入力は捨てない）
      if (newHydratingRef.current) {
        if (skipNextNewStartDateSnapshotRef.current) {
          skipNextNewStartDateSnapshotRef.current = false;
        }
        if (isWipingNonEmpty) return;
      }

      // 初回同期フラグは「空で潰す」だけ抑止し、非空は通常ルートへ通す（= ユーザー入力を取りこぼさない）
      if (skipNextNewStartDateSnapshotRef.current) {
        skipNextNewStartDateSnapshotRef.current = false;
        if (isWipingNonEmpty) return;
      }

      // Footer クリア直後の「旧値復活」を クリア直前の値と一致する場合のみ拒否（新規選択は許可）
      if (datesClearedRef.current && vv !== '') {
        const withinGuard = Date.now() - datesClearedAtRef.current < DATE_CLEAR_GUARD_MS;

        if (withinGuard) {
          const curStartEmpty = normalizeDateYmd(startDate) === '';
          const curEndEmpty = normalizeDateYmd(endDate) === '';
          const last = lastNonEmptyDatesRef.current;
          const lastStart = normalizeDateYmd(last.start);
          if (curStartEmpty && curEndEmpty && lastStart && vv === lastStart) return;
        }

        // 新規選択なら解除（ガード窓外なら同値でも許可）
        datesClearedRef.current = false;
        clearedDatesPersistedRef.current = false;
      }

      if (vv === cur) return; // ★追加：同値なら何もしない
      setStartDate(vv);

      // start/end を保存するだけでなく、fields の「開始終了日付」も同期して保存
      const base = newDraftCacheRef.current;
      const df = Array.isArray(base?.fields)
        ? (base!.fields as any[]).find((f: any) => String(f?.type ?? '').trim() === '開始終了日付')
        : null;

      const endFromFieldsRaw = String((df as any)?.value?.to ?? '').trim();
      const endFromFields = normalizeDateYmd(endFromFieldsRaw);

      const endToSync =
        normalizeDateYmd(endDate) ||
        normalizeDateYmd(base?.endDate) ||
        endFromFields;

      const patchedFields = patchDateRangeFieldInDraftFields(base?.fields, vv, endToSync);

      const patch: Partial<LastNewDraftCache> = { startDate: vv };
      if (patchedFields) patch.fields = patchedFields;

      persistNewDraft(patch);
    },
    [setStartDate, persistNewDraft, startDate, endDate]
  );

  const handleNewEndDateChange = useCallback(
    (v: string) => {
      if (latestActiveTabRef.current !== 'new') return;

      const vv = normalizeDateYmd(v);
      const cur = normalizeDateYmd(endDate);

      const isWipingNonEmpty = vv === '' && cur !== '';

      if (newHydratingRef.current) {
        if (skipNextNewEndDateSnapshotRef.current) skipNextNewEndDateSnapshotRef.current = false;
        if (isWipingNonEmpty) return;
      }

      if (skipNextNewEndDateSnapshotRef.current) {
        skipNextNewEndDateSnapshotRef.current = false;
        if (isWipingNonEmpty) return;
      }

      // Footer クリア直後の「旧値復活」を クリア直前の値と一致する場合のみ拒否（新規選択は許可）
      if (datesClearedRef.current && vv !== '') {
        const withinGuard = Date.now() - datesClearedAtRef.current < DATE_CLEAR_GUARD_MS;

        if (withinGuard) {
          const curStartEmpty = normalizeDateYmd(startDate) === '';
          const curEndEmpty = normalizeDateYmd(endDate) === '';
          const last = lastNonEmptyDatesRef.current;
          const lastEnd = normalizeDateYmd(last.end);
          if (curStartEmpty && curEndEmpty && lastEnd && vv === lastEnd) return;
        }

        // 新規選択なら解除（ガード窓外なら同値でも許可）
        datesClearedRef.current = false;
        clearedDatesPersistedRef.current = false;
      }

      if (vv === cur) return;
      setEndDate(vv);

      // ここでは fields を patch/persist しない（snapshot 側で保存）
      persistNewDraft({ endDate: vv });
    },
    [setEndDate, persistNewDraft, endDate, startDate]
  );

  const clearNewDatesSync = useCallback(() => {
    if (latestActiveTabRef.current !== 'new') return;

    const deptId = latestDeptIdRef.current;
    if (typeof deptId !== 'number') return;

    const prevStart = normalizeDateYmd(startDate);
    const prevEnd = normalizeDateYmd(endDate);

    if (prevStart || prevEnd) {
      lastNonEmptyDatesRef.current = { start: prevStart, end: prevEnd };
    }

    datesClearedRef.current = true;
    datesClearedAtRef.current = Date.now();

    clearedDatesPersistedRef.current = true;

    setStartDate('');
    setEndDate('');

    // ★ 修正：newDraftCacheRef が null のタイミングでも、
    // 既存キャッシュ（記入者/承認者/チーム等）を落とさないようにベースを復元してから patch する
    const base =
      newDraftCacheRef.current ??
      readLastNewDraftCache(userCacheKey, deptId) ??
      null;

    if (base) newDraftCacheRef.current = base;

    const clearedFields = patchDateRangeFieldInDraftFields(base?.fields, '', '');

    const patch: Partial<LastNewDraftCache> = {
      ...(base ?? {}),
      startDate: '',
      endDate: '',
    };
    if (clearedFields) patch.fields = clearedFields;

    persistNewDraft(patch);
  }, [
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    persistNewDraft,
    userCacheKey,
  ]);

  // =========================================================
  // Footer(送信成功) から NEW の日付を「UI(state)ごと」クリアできるように、
  // SearchArea 側の clear 関数を親へ登録する。
  // （これが無いと localStorage は消えても startDate/endDate が残って表示が消えない）
  // =========================================================
  useEffect(() => {
    onProvideClearNewDates?.(clearNewDatesSync);
  }, [onProvideClearNewDates, clearNewDatesSync]);

  /* -----------------------------
   * NEW の taskMembers 変更を親がフックしてキャッシュ保存
   *   - SearchArea1st には setTaskMembers として渡す
   * --------------------------- */
  const handleNewTaskMembersChange = useCallback(
    (nextOrUpdater: React.SetStateAction<any[] | undefined>) => {
      if (latestActiveTabRef.current !== 'new') return;

      const next =
        typeof nextOrUpdater === 'function'
          ? (nextOrUpdater as any)(taskMembersRef.current)
          : nextOrUpdater;

      const sanitized = sanitizeTaskMembers(next);

      // 復元直後など、初回の同期で空が来ても上書きしない（1回だけスキップ）
      if (skipNextNewTaskMembersSnapshotRef.current) {
        skipNextNewTaskMembersSnapshotRef.current = false;

        const incomingEmpty = !Array.isArray(sanitized) || sanitized.length === 0;
        const existingHas = Array.isArray(taskMembersRef.current) && taskMembersRef.current.length > 0;

        // “空で潰す” だけ抑止し、非空や差分は通常処理へ通す（= ユーザー操作を取りこぼさない）
        if (incomingEmpty && existingHas) return;
      }

      // 同じなら state も保存もしない（子の同期ループで無限に上書きされるのを防ぐ）
      if (areTaskMembersEqual(taskMembersRef.current, sanitized)) return;

      setTaskMembers(sanitized);
      persistNewDraft({ taskMembers: sanitized });
    },
    [persistNewDraft]
  );

  /* -----------------------------
   * 5) team 自動確定
   * --------------------------- */
  const teamAutoAppliedForDeptRef = useRef<number | null>(null);
  useEffect(() => {
    if (typeof resolvedDeptId !== 'number') return;
    if (!Array.isArray(deptTeamLinks) || deptTeamLinks.length === 0) return;

    const normalizeKey = (s?: string) =>
      (s ?? '').replace(/チーム$/, '').replace(/[\s\u3000]+/g, ' ').trim().toUpperCase();

    const first = deptTeamLinks[0];
    const firstName = first?.team_value || first?.team_label || '';

    const teamProp = (team ?? '').trim();

    let nextTeamId: number | null = null;

    // NEWタブでは「前回選択チーム」を最優先で復元（存在する＆候補にある場合のみ）
    if (!isOverwrite && teamProp === '') {
      const cached = readLastNewTeamCache(userCacheKey, resolvedDeptId);
      if (cached) {
        const byId =
          typeof cached.team_id === 'number'
            ? deptTeamLinks.find((l) => Number(l.team_id) === Number(cached.team_id))
            : null;

        const byText =
          !byId && cached.team
            ? deptTeamLinks.find((l) => {
                const key = normalizeKey(cached.team || '');
                return (
                  normalizeKey(l.team_label || '') === key ||
                  normalizeKey(l.team_value || '') === key ||
                  String(l.team_id) === String(cached.team || '')
                );
              })
            : null;

        const hit = byId ?? byText;

        if (hit) {
          const name = hit?.team_value || hit?.team_label || cached.team || '';
          nextTeamId = typeof hit?.team_id === 'number' ? hit.team_id : null;

          if (setTeam && name && team !== name) setTeam(name);
          if (resolvedTeamId !== (typeof nextTeamId === 'number' ? nextTeamId : null)) {
            setResolvedTeamId(typeof nextTeamId === 'number' ? nextTeamId : null);
          }

          teamAutoAppliedForDeptRef.current = resolvedDeptId;
          return;
        }
      }
    }

    // 未指定("")のときだけ「同じ部門では 1 回だけ」スキップを適用
    if (teamProp === '' && teamAutoAppliedForDeptRef.current === resolvedDeptId) return;

    if (teamProp !== '') {
      const key = normalizeKey(teamProp);
      const hit = deptTeamLinks.find(
        (l) =>
          normalizeKey(l.team_label || '') === key ||
          normalizeKey(l.team_value || '') === key ||
          String(l.team_id) === teamProp
      );
      nextTeamId = typeof hit?.team_id === 'number' ? hit.team_id : first.team_id;

      if (!hit && setTeam && firstName && team !== firstName) setTeam(firstName);
    } else {
      nextTeamId = first.team_id;
      if (setTeam && firstName && team !== firstName) setTeam(firstName);
    }

    if (resolvedTeamId !== (typeof nextTeamId === 'number' ? nextTeamId : null)) {
      setResolvedTeamId(typeof nextTeamId === 'number' ? nextTeamId : null);
    }

    if (teamProp === '') teamAutoAppliedForDeptRef.current = resolvedDeptId;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deptTeamLinks, resolvedDeptId, team, isOverwrite, userCacheKey]);

  const getNewSelectionForCurrentDept = (): {
    division: string;
    team: string;
    teamId: number | null;
  } => {
    const deptId = typeof resolvedDeptId === 'number' ? resolvedDeptId : null;
    const cachedTeam = deptId !== null ? readLastNewTeamCache(userCacheKey, deptId) : null;
    const cachedDraft = deptId !== null ? readLastNewDraftCache(userCacheKey, deptId) : null;

    const cachedTeamId =
      toNum(cachedTeam?.team_id) ??
      toNum(cachedDraft?.team_id) ??
      null;
    const cachedTeamName = String(
      cachedTeam?.team ??
        cachedDraft?.team ??
        team ??
        lastNewSelectionRef.current.team ??
        ''
    ).trim();
    const idFromName = resolveTeamIdByHint(cachedTeamName, deptTeamLinks);
    const teamId = cachedTeamId ?? idFromName ?? null;
    const link =
      teamId !== null
        ? deptTeamLinks.find((l) => Number(l?.team_id) === Number(teamId))
        : null;
    const linkedName = String(link?.team_value ?? link?.team_label ?? '').trim();
    const teamName = linkedName || cachedTeamName;
    const divisionValue =
      (deptId !== null ? getDivisionValueByDeptId(deptId) : null) ??
      lastNewSelectionRef.current.division ??
      division;

    return {
      division: divisionValue,
      team: teamName,
      teamId,
    };
  };

  /* -----------------------------
   * 5.5) OVERWRITE → NEW へ戻ったら（NEW側の division/team を復元）
   *  + NEW → OVERWRITE へ切り替えたら（OVERWRITE側の検索条件を復元）
   * --------------------------- */
  const prevTabRef = useRef<'new' | 'overwrite' | null>(null);

  useEffect(() => {
    const prev = prevTabRef.current;
    const now = activeTab;

    // NEW → OVERWRITE に切り替えたら、OVERWRITE 側の検索条件を復元する
    // NEW送信成功で start/end が空にされても、ここで戻す
    if (now === 'overwrite' && prev === 'new') {
      const keepSel = lastOverwriteSelectionRef.current;
      const keepFixed = lastOverwriteFixedRef.current;

      // division / team を復元（以前の overwrite 条件に戻す）
      if (keepSel?.division && normUpper(division) !== normUpper(keepSel.division)) {
        setDivision(keepSel.division);
      }
      if (setTeam && String(team ?? '') !== String(keepSel?.team ?? '')) {
        setTeam(String(keepSel?.team ?? ''));
      }

      // start/end を復元（空でなければ戻す）
      const s = String(keepFixed?.startDate ?? '').trim();
      const e = String(keepFixed?.endDate ?? '').trim();

      if (s && normalizeDateYmd(startDate) !== normalizeDateYmd(s)) {
        setStartDate(s);
      }
      if (e && normalizeDateYmd(endDate) !== normalizeDateYmd(e)) {
        setEndDate(e);
      }
    }

    // OVERWRITE → NEW に戻ったら、NEW の状態を復元する
    if (now === 'new' && prev === 'overwrite') {
      // Overwrite 側の選択payloadをクリアする。
      // 動的フィールドは親側で New / Overwrite ごとに分離して保持しているため、
      // New 側の復元値をここで空にする必要はない。
      searchSelectionRef.current = null;
      onSearchPayloadChange?.(null);

      setOverwritePrefill(null);

      // 戻った直後に child が吐く “空スナップショット” で NEW 下書きが潰れるのを防ぐ
      skipNextNewSnapshotRef.current = true;
      skipNextNewTaskMembersSnapshotRef.current = true;
      skipNextNewFreeWordsSnapshotRef.current = true;
      // 日付も初回空で潰さない
      skipNextNewStartDateSnapshotRef.current = true;
      skipNextNewEndDateSnapshotRef.current = true;

      lockNewHydration();

      // NEW の最後の選択を復元（Overwrite 側で潰れた場合に戻す）
      const keep = getNewSelectionForCurrentDept();

      if (keep?.division && normUpper(division) !== normUpper(keep.division)) {
        setDivision(keep.division);
      }
      if (setTeam && keep?.team && String(team ?? '') !== String(keep.team)) {
        setTeam(String(keep.team));
      }

      pendingNewRemountRef.current = true;
      restoredNewDraftKeyRef.current = null;

      // master確定後の remount 判定もやり直す
      appliedNewMasterAttrSigRef.current = null;

      // NEW: 同一比較の基準をリセット（復元後に必要なら再計算される）
      lastPersistedNewDraftJsonRef.current = '';

      // NEW: fields snapshot 比較もリセット
      lastNewFieldsSnapshotJsonRef.current = '';

      setNewRestoreKick((s) => s + 1);

      // NEW の固定項目も復元（承認者が上書きされないように）
      const fixed = lastNewFixedRef.current;

      // 日付も（localStorage復元に加えて、タブ往復では保持する）
      if (
        typeof fixed?.startDate !== 'undefined' &&
        String(startDate ?? '') !== String(fixed.startDate ?? '')
      ) {
        setStartDate(String(fixed.startDate ?? ''));
      }
      if (
        typeof fixed?.endDate !== 'undefined' &&
        String(endDate ?? '') !== String(fixed.endDate ?? '')
      ) {
        setEndDate(String(fixed.endDate ?? ''));
      }

      if (
        typeof fixed?.reporter !== 'undefined' &&
        String(reporter ?? '') !== String(fixed.reporter ?? '')
      ) {
        setReporter(String(fixed.reporter ?? ''));
      }
      if (
        typeof fixed?.approver3 !== 'undefined' &&
        String(approver3 ?? '') !== String(fixed.approver3 ?? '')
      ) {
        setApprover3(String(fixed.approver3 ?? ''));
      }
      if (
        typeof fixed?.approverAM !== 'undefined' &&
        String(approverAM ?? '') !== String(fixed.approverAM ?? '')
      ) {
        setApproverAM(String(fixed.approverAM ?? ''));
      }
      if (
        setReportType &&
        typeof fixed?.reportType !== 'undefined' &&
        String(reportType ?? '') !== String(fixed.reportType ?? '')
      ) {
        setReportType(String(fixed.reportType ?? ''));
      }
      if (
        typeof fixed?.shikimuke !== 'undefined' &&
        String(shikimuke ?? '') !== String(fixed.shikimuke ?? '')
      ) {
        setShikimuke(String(fixed.shikimuke ?? ''));
      }
      if (
        typeof fixed?.lot !== 'undefined' &&
        String(lot ?? '') !== String(fixed.lot ?? '')
      ) {
        setLot(String(fixed.lot ?? ''));
      }

      // taskMembers も戻す（NEW の状態を潰さない）
      if (typeof fixed?.taskMembers !== 'undefined') {
        setTaskMembers(fixed.taskMembers);
        taskMembersRef.current = fixed.taskMembers;
      }

      let nextNewTeamId = keep.teamId;
      let nextNewTeamName = keep.team;
      if (nextNewTeamId === null && deptTeamLinks.length > 0) {
        const first = deptTeamLinks[0];
        nextNewTeamId = typeof first?.team_id === 'number' ? first.team_id : null;
        if (!nextNewTeamName) {
          nextNewTeamName = String(first?.team_value ?? first?.team_label ?? '').trim();
        }
      }

      if (setTeam && nextNewTeamName && String(team ?? '') !== String(nextNewTeamName)) {
        setTeam(nextNewTeamName);
      }
      setResolvedTeamId(nextNewTeamId);

      teamAutoAppliedForDeptRef.current = null;
    }

    prevTabRef.current = now as 'new' | 'overwrite' | null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // 保険：lockNewHydration したまま残らないように、次tickで解除する
  useEffect(() => {
    if (activeTab !== 'new') return;
    if (!newHydratingRef.current) return;

    const t = window.setTimeout(() => {
      unlockNewHydration();
    }, 0);

    return () => {
      window.clearTimeout(t);
    };
  }, [activeTab, newFormResetSeq, newRestoreKick, unlockNewHydration]);

  /* -----------------------------
   * 5.7) 課(dept) 変更時に承認者クリア(Overwriteタブのみ)
   * --------------------------- */
  const prevDeptIdRefForOverwrite = useRef<number | null>(null);
  useEffect(() => {
    const currDeptId = typeof resolvedDeptId === 'number' ? resolvedDeptId : null;
    const prevDeptId = prevDeptIdRefForOverwrite.current;

    // Overwriteタブ上で「課」が変わったときだけ動く
    if (activeTab === 'overwrite' && currDeptId !== null && prevDeptId !== null && currDeptId !== prevDeptId) {
      // 承認者など、課に依存する人名はクリアする
      setApprover3('');
      setApproverAM('');

      // payload側からも古い承認者を消す
      const current = (searchSelectionRef.current ?? { fixed: {}, entries: [] }) as any;
      const patched = {
        ...current,
        fixed: {
          ...(current.fixed ?? {}),
          approver3: null,
          approverAM: null,
        },
      };
      searchSelectionRef.current = patched;
      onSearchPayloadChange?.(patched);

      // OverwriteInputSearchArea 側の初期表示用データ(prefill)もクリアしておく
      // → 次のマウント時には空の承認者でスタートさせたい
      setOverwritePrefill((old) => {
        if (!old) return old;
        return {
          ...old,
          approver3: '',
          approverAM: '',
        };
      });
    }

    prevDeptIdRefForOverwrite.current = currDeptId;
  }, [resolvedDeptId, activeTab, setApprover3, setApproverAM, onSearchPayloadChange]);

  /* -----------------------------
   * 6) マスター取得
   * --------------------------- */
  useEffect(() => {
    const requestSeq = ++masterRequestSeqRef.current;

    if (typeof resolvedDeptId !== 'number' || typeof resolvedTeamId !== 'number') {
      setMasterLoading(false);
      return;
    }

    const nextKey = `${resolvedDeptId}:${resolvedTeamId}`;
    if (lastFetchKeyRef.current === nextKey) {
      if (!ready) setReady(true);
      setMasterLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        setMasterLoading(true);
        setError(null);

        const fetchMaster = async (teamId: number): Promise<WeeklyMasterResponse> => {
          const res = await shareInFlightRequest(
            `weekly-input-items:${resolvedDeptId}:${teamId}`,
            () => apiClient.get<WeeklyMasterResponse>(WEEKLY_INPUT_ITEMS_PATH, {
              headers: { Accept: 'application/json' },
              params: { department_id: resolvedDeptId, team_id: teamId },
            }),
          );

          return res.data;
        };

        const [primaryJson, publicSettings] = await Promise.all([
          fetchMaster(resolvedTeamId),
          fetchLatestWeeklyReportPublicSettings().catch(() => null),
        ]);

        // 旧APIが team_id 完全一致で ATTRIBUTE_MANAGE を返す場合、
        // team_id=41 などのチーム選択時に department_id=14/team_id=0 の
        // 課共通属性（例: 工順）がレスポンスから落ちる。
        // そのため、チーム選択中だけ team_id=0 も追加取得してマージする。
        const fallbackJson =
          resolvedTeamId > 0
            ? await fetchMaster(0).catch(() => null)
            : null;

        if (cancelled || !mountedRef.current || masterRequestSeqRef.current !== requestSeq) return;

        const json = mergeWeeklyMasterResponses(primaryJson, fallbackJson);

        setMaster((prev) => ({
          departments: json.departments ?? [],
          teams: normalizeTeamFiscalYears(json.teams),
          attributeManage: (json as any).attributeManage ?? (json as any).attributes ?? [],
          attributes: (json as any).attributes ?? (json as any).attributeManage ?? [],
          attrMap: (json as any).attrMap ?? {},
          attributeBuckets: normalizeAttributeBuckets(
            (json as any).attributeBuckets ?? (json as any).buckets
          ),
          buckets: (json as any).buckets ?? undefined,
          creators: json.creators ?? [],
          approvers: prev?.approvers ?? (json as any).approvers ?? [],
          approverAMs: prev?.approverAMs ?? (json as any).approverAMs ?? [],
          taskMembers: (json as any).taskMembers ?? [],
          wrPublicSettings:
            publicSettings ?? prev?.wrPublicSettings ?? [],
        }));

        lastFetchKeyRef.current = nextKey;
        if (!ready) setReady(true);
      } catch (err: any) {
        if (cancelled || !mountedRef.current || masterRequestSeqRef.current !== requestSeq) return;

        setError(err?.message ?? String(err));
        setMaster(null);
        if (!ready) setReady(true);
      } finally {
        if (mountedRef.current && masterRequestSeqRef.current === requestSeq) {
          setMasterLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedDeptId, resolvedTeamId, ready]);

  const currentMasterKey =
    typeof resolvedDeptId === 'number' && typeof resolvedTeamId === 'number'
      ? `${resolvedDeptId}:${resolvedTeamId}`
      : null;
  const masterReadyForCurrentSelection =
    !!master &&
    !!currentMasterKey &&
    lastFetchKeyRef.current === currentMasterKey;

  // 公開範囲は他の画面・別タブから変更されるため、入力画面を開いたままでも
  // 再表示時に最新値へ置き換える。連続イベントは同じ再取得にまとめる。
  useEffect(() => {
    if (!masterReadyForCurrentSelection) return undefined;

    let cancelled = false;

    const refreshPublicSettings = () => {
      void fetchLatestWeeklyReportPublicSettings()
        .then((rows) => {
          if (cancelled || !mountedRef.current) return;
          setMaster((current) =>
            current ? { ...current, wrPublicSettings: rows } : current
          );
        })
        .catch((refreshError) => {
          if (cancelled) return;
          // 再取得失敗時は現在の表示を維持し、次回の再表示で再試行する。
          console.error('/get_wr_public_setting の再取得に失敗:', refreshError);
        });
    };

    return subscribeWeeklyReportPublicScopeRefresh(refreshPublicSettings);
  }, [masterReadyForCurrentSelection, currentMasterKey]);

  // 週報分類などのマスタは別タブのマスタ管理で追加・変更されるため、入力画面を開いたままでも
  // 更新通知（event/storage）と画面復帰時に最新値へ置き換える。
  // 画面復帰（focus/visibility）は通信が増えすぎないよう、前回取得から一定時間以上空いた場合のみ再取得する。
  useEffect(() => {
    if (!masterReadyForCurrentSelection) return undefined;
    if (typeof resolvedDeptId !== 'number' || typeof resolvedTeamId !== 'number') return undefined;

    const deptId = resolvedDeptId;
    const teamId = resolvedTeamId;
    let cancelled = false;
    let lastRefreshedAt = Date.now();

    const refreshMasterItems = (reason: WeeklyReportPublicScopeRefreshReason) => {
      const now = Date.now();
      const passive = reason === 'focus' || reason === 'visibility';
      if (passive && now - lastRefreshedAt < MASTER_PASSIVE_REFRESH_MIN_INTERVAL_MS) return;
      lastRefreshedAt = now;

      const fetchMaster = async (targetTeamId: number): Promise<WeeklyMasterResponse> => {
        const res = await apiClient.get<WeeklyMasterResponse>(WEEKLY_INPUT_ITEMS_PATH, {
          headers: { Accept: 'application/json' },
          params: { department_id: deptId, team_id: targetTeamId },
        });
        return res.data;
      };

      void (async () => {
        try {
          const primaryJson = await fetchMaster(teamId);
          const fallbackJson = teamId > 0 ? await fetchMaster(0).catch(() => null) : null;
          if (cancelled || !mountedRef.current) return;

          const json = mergeWeeklyMasterResponses(primaryJson, fallbackJson);
          const attributeManage = (json as any).attributeManage ?? (json as any).attributes ?? [];
          const attributes = (json as any).attributes ?? (json as any).attributeManage ?? [];
          const attributeBuckets = normalizeAttributeBuckets(
            (json as any).attributeBuckets ?? (json as any).buckets
          );

          setMaster((current) => {
            if (!current) return current;
            // 内容が変わっていなければ state を更新しない（入力中の画面を再描画させない）
            const unchanged =
              JSON.stringify(current.attributeBuckets ?? []) === JSON.stringify(attributeBuckets) &&
              JSON.stringify(current.attributeManage ?? []) === JSON.stringify(attributeManage);
            if (unchanged) return current;
            return {
              ...current,
              attributeManage,
              attributes,
              attrMap: (json as any).attrMap ?? {},
              attributeBuckets,
              buckets: (json as any).buckets ?? undefined,
            };
          });
        } catch (refreshError) {
          if (cancelled) return;
          // 再取得失敗時は現在の表示を維持し、次回の通知・再表示で再試行する。
          console.error('/weekly-input-items の再取得に失敗:', refreshError);
        }
      })();
    };

    const unsubscribe = subscribeWeeklyReportPublicScopeRefresh(refreshMasterItems);
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [masterReadyForCurrentSelection, currentMasterKey, resolvedDeptId, resolvedTeamId]);

  // NEWタブで master（動的属性）が後から確定/更新されたら、1回だけ remount して draft 初期値を確実に反映
  const newMasterAttrSig = useMemo(() => {
    if (typeof resolvedDeptId !== 'number') return null;
    if (typeof resolvedTeamId !== 'number') return null;
    if (!master) return null;

    const list = (master?.attributeManage ?? master?.attributes ?? []) as any[];
    const sig = list.map((a: any) => `${toNum(a?.attr_id) ?? ''}:${String(a?.attr_name ?? '')}`).join('|');

    return `d${resolvedDeptId}-t${resolvedTeamId}-${sig}`;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedDeptId, resolvedTeamId, master?.attributeManage, master?.attributes]);

  useEffect(() => {
    if (activeTab !== 'new') return;
    if (!ready) return;
    if (!newMasterAttrSig) return;

    // 同一master構成での二重remount防止
    if (appliedNewMasterAttrSigRef.current === newMasterAttrSig) return;

    const hasDraftFields = Array.isArray(initialNewDraftFields) && initialNewDraftFields.length > 0;
    const hasDraftFreeWords = hasAnyFreeWordsValue(initialNewDraftFreeWords);

    // draftが何も無いなら remount しない（無駄な再構築を避ける）
    if (!hasDraftFields && !hasDraftFreeWords) {
      appliedNewMasterAttrSigRef.current = newMasterAttrSig;
      return;
    }

    // remount前に空スナップショット上書きを封じる
    lockNewHydration();
    skipNextNewSnapshotRef.current = true;
    skipNextNewTaskMembersSnapshotRef.current = true;
    skipNextNewFreeWordsSnapshotRef.current = true;
    // 日付も同様に抑止
    skipNextNewStartDateSnapshotRef.current = true;
    skipNextNewEndDateSnapshotRef.current = true;

    appliedNewMasterAttrSigRef.current = newMasterAttrSig;
    setNewFormResetSeq((s) => s + 1);
  }, [
    activeTab,
    ready,
    newMasterAttrSig,
    initialNewDraftFields,
    initialNewDraftFreeWords,
    lockNewHydration,
  ]);

  // 6.5) 承認者一覧の取得
  useEffect(() => {
    if (typeof resolvedDeptId !== 'number') return;
    if (!ready) return;

    let cancelled = false;

    (async () => {
      try {
        const [resA3, resAM] = await Promise.all([
          shareInFlightRequest(
            `approve3-members:${resolvedDeptId}`,
            () => apiClient.get(`${APPROVE3_MEMBERS_PATH}`, {
              params: { department_id: resolvedDeptId },
            }),
          ),
          shareInFlightRequest(
            `approveam-members:${resolvedDeptId}`,
            () => apiClient.get(`${APPROVEAM_MEMBERS_PATH}`, {
              params: { department_id: resolvedDeptId },
            }),
          ),
        ]);

        if (cancelled || !mountedRef.current) return;

        const normalizeList = (data: any): any[] => {
          return Array.isArray(data)
            ? data
            : Array.isArray((data ?? {})?.list)
            ? (data as any).list
            : Array.isArray((data ?? {})?.data)
            ? (data as any).data
            : Array.isArray((data ?? {})?.users)
            ? (data as any).users
            : Array.isArray((data ?? {})?.items)
            ? (data as any).items
            : [];
        };

        const toListApprove3 = (data: any, deptId: number): Array<{ label: string; value: string }> => {
          const src = normalizeList(data);
          const seen = new Set<string>();
          const out: Array<{ label: string; value: string }> = [];
          src.forEach((x) => {
            if (Number(x?.approve_3) !== 1 && Number(x?.approve3) !== 1) return;
            const deptOk = typeof x?.department_id !== 'number' || Number(x.department_id) === deptId;
            if (!deptOk) return;
            const name = String((x?.name ?? x?.label ?? x?.value ?? x) ?? '').trim();
            if (!name || seen.has(name)) return;
            seen.add(name);
            out.push({ label: name, value: name });
          });
          return out;
        };

        const toListApproveAm = (data: any, deptId: number): Array<{ label: string; value: string }> => {
          const src = normalizeList(data);
          const seen = new Set<string>();
          const out: Array<{ label: string; value: string }> = [];
          src.forEach((x) => {
            if (Number(x?.approve_am) !== 1 && Number(x?.approveAm) !== 1) return;
            const deptOk = typeof x?.department_id !== 'number' || Number(x.department_id) === deptId;
            if (!deptOk) return;
            const name = String((x?.name ?? x?.label ?? x?.value ?? x) ?? '').trim();
            if (!name || seen.has(name)) return;
            seen.add(name);
            out.push({ label: name, value: name });
          });
          return out;
        };

        const a3 = toListApprove3(resA3?.data, resolvedDeptId);
        const am = toListApproveAm(resAM?.data, resolvedDeptId);

        setMaster((prev) => (prev ? { ...prev, approvers: a3, approverAMs: am } : prev));
      } catch {
        // フォールバック：weekly-input-items の値を使う
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedDeptId, ready]);

  /* -----------------------------
   * 表示候補
   * --------------------------- */
  const displayDivisionOptions = useMemo(
    () => (scopedDivisionOptions.length ? scopedDivisionOptions : divisionOptions),
    [scopedDivisionOptions, divisionOptions]
  );

  /* -----------------------------
   * 子へ渡すハンドラの参照を安定化
   * --------------------------- */

  // NEW 用：hidden 中に発火しても親を汚さない（activeTab ガード）
  const handleNewFieldsSnapshot = useCallback(
    (fields: any[]) => {
      if (latestActiveTabRef.current !== 'new') return;

      // hydration 中は「子→親」を完全停止（update depth 抑止）
      if (newHydratingRef.current) return;

      // 初回の“空スナップショット”で上書きしない
      if (skipNextNewSnapshotRef.current) {
        skipNextNewSnapshotRef.current = false;
        return;
      }

      const deptId = latestDeptIdRef.current;
      if (typeof deptId !== 'number') return;

      const scopedFieldsForParent = sanitizeScopedDynamicFieldsForParent(fields);
      const sanitized = sanitizeDraftFields(scopedFieldsForParent);
      const tm = sanitizeTaskMembers(taskMembersRef.current);

      // fields 内の開始/終了日付（開始終了日付）も保存（top-level と併用）
      const dateField = sanitized.find((f: any) => String(f?.type ?? '').trim() === '開始終了日付');
      const startFromFieldsRaw = String((dateField as any)?.value?.from ?? '').trim();
      const endFromFieldsRaw = String((dateField as any)?.value?.to ?? '').trim();
      const startState = normalizeDateYmd(startDate);
      const endState = normalizeDateYmd(endDate);

      const startToSave = datesClearedRef.current ? '' : (startState || normalizeDateYmd(startFromFieldsRaw));
      const endToSave = datesClearedRef.current ? '' : (endState || normalizeDateYmd(endFromFieldsRaw));

      // fields の daterange を top-level と同期して保存する
      const patchedFields =
        patchDateRangeFieldInDraftFields(sanitized, startToSave, endToSave) ?? sanitized;

      const textFromDraftValue = (value: any): string => {
        if (Array.isArray(value)) {
          return value
            .map((item) => textFromDraftValue(item))
            .filter(Boolean)
            .join(',');
        }

        if (value && typeof value === 'object') {
          return String((value as any).label ?? (value as any).value ?? '').trim();
        }

        return String(value ?? '').trim();
      };

      const fieldTextOr = (type: string, fallback: unknown): string => {
        const key = type.normalize('NFKC').trim();
        const hit = patchedFields.find(
          (field: any) => String(field?.type ?? '').normalize('NFKC').trim() === key
        );
        const text = hit ? textFromDraftValue((hit as any).value) : '';
        return text || String(fallback ?? '');
      };

      const reporterToSave = fieldTextOr('記入者', reporter);
      const reportTypeToSave = fieldTextOr(CLASSIFICATION_MARKET_TARGET_ATTR_NAME, reportType);
      const shikimukeToSave = fieldTextOr(FIXED_ATTR_NAME_SHIMUKE, shikimuke);
      const lotToSave = fieldTextOr(FIXED_ATTR_NAME_LOT, lot);

      // fw は ref / initial のどちらかが生きていればそれを維持（空で潰さない）
      const fwFromRef = sanitizeFreeWords(freeWordsRef.current);
      const fwFromInitial = sanitizeFreeWords(initialNewDraftFreeWords ?? {});
      const fw = hasAnyFreeWordsValue(fwFromRef)
        ? fwFromRef
        : hasAnyFreeWordsValue(fwFromInitial)
        ? fwFromInitial
        : fwFromRef;

      // 同一スナップショットなら何もしない（親の setState 連鎖を止める）
      const snapshotCompare = {
        team: String(team ?? ''),
        team_id: typeof resolvedTeamId === 'number' ? resolvedTeamId : null,
        reporter: reporterToSave,
        approver3: approver3 ?? '',
        approverAM: approverAM ?? '',
        reportType: reportTypeToSave,
        shikimuke: shikimukeToSave,
        lot: lotToSave,
        startDate: startToSave,
        endDate: endToSave,
        taskMembers: tm,
        fields: patchedFields,
        freeWords: fw,
      };
      const snapshotJson = stableStringify(snapshotCompare);

      if (snapshotJson === lastNewFieldsSnapshotJsonRef.current) return;
      lastNewFieldsSnapshotJsonRef.current = snapshotJson;

      const draftPatch: Partial<LastNewDraftCache> = {
        team: String(team ?? ''),
        reporter: reporterToSave,
        approver3: approver3 ?? '',
        approverAM: approverAM ?? '',
        reportType: reportTypeToSave,
        shikimuke: shikimukeToSave,
        lot: lotToSave,
        startDate: startToSave,
        endDate: endToSave,
        taskMembers: tm,
        fields: patchedFields,
        freeWords: fw,
      };

      if (typeof resolvedTeamId === 'number') {
        draftPatch.team_id = resolvedTeamId;
      }

      persistNewDraft(draftPatch);

      // 同一でない場合だけ親へ通知（無限ループ抑止）
      onDynamicFieldsChange?.(scopedFieldsForParent);

    },
    [
      onDynamicFieldsChange,
      sanitizeScopedDynamicFieldsForParent,
      team,
      resolvedTeamId,
      reporter,
      approver3,
      approverAM,
      reportType,
      shikimuke,
      lot,
      startDate,
      endDate,
      initialNewDraftFreeWords,
      persistNewDraft,
    ]
  );

  // OVERWRITE 用：hidden 中に発火しても親を汚さない（activeTab ガード）
  const handleOverwriteFieldsSnapshot = useCallback(
    (fields: any[]) => {
      if (latestActiveTabRef.current !== 'overwrite') return;
      onDynamicFieldsChange?.(sanitizeScopedDynamicFieldsForParent(fields));
    },
    [onDynamicFieldsChange, sanitizeScopedDynamicFieldsForParent]
  );

  const handleOverwriteFreeWordsChange = useCallback(
    (fw: Record<string, string>) => {
      if (latestActiveTabRef.current !== 'overwrite') return;
      onFreeWordsChange?.(fw);
    },
    [onFreeWordsChange]
  );

  const handleNewAttributesChange = useCallback(
    (attrs: Array<{ attr_id: number; attr_name: string; attr_value: string }>) => {
      if (latestActiveTabRef.current !== 'new') return;

      const scopedAttrs = sanitizeScopedDynamicAttrs(attrs);
      onAttributesChange?.(scopedAttrs);
    },
    [onAttributesChange, sanitizeScopedDynamicAttrs]
  );

  const handleOverwriteAttributesChange = useCallback(
    (attrs: Array<{ attr_id: number; attr_name: string; attr_value: string }>) => {
      if (latestActiveTabRef.current !== 'overwrite') return;

      const scopedAttrs = sanitizeScopedDynamicAttrs(attrs);
      onAttributesChange?.(scopedAttrs);
    },
    [onAttributesChange, sanitizeScopedDynamicAttrs]
  );

  const handleNewFreeWordsChange = useCallback(
    (fw: Record<string, string>) => {
      if (latestActiveTabRef.current !== 'new') return;

      const sanitized = sanitizeFreeWords(fw);

      // 初回の空スナップショットは「空で潰す」だけ抑止（非空は通常処理へ通す）
      if (skipNextNewFreeWordsSnapshotRef.current) {
        skipNextNewFreeWordsSnapshotRef.current = false;

        const incomingHas = hasAnyFreeWordsValue(sanitized);
        const existingHas = hasAnyFreeWordsValue(freeWordsRef.current);

        if (!incomingHas && existingHas) return;
      }

      // 親へ通知（NEWのときのみ）
      onFreeWordsChange?.(fw);

      if (areFreeWordsEqual(freeWordsRef.current, sanitized)) return;

      freeWordsRef.current = sanitized;
      // sinitial は “初期値” 用。入力のたびに更新しない（子の再初期化ループを避ける）
      persistNewDraft({ freeWords: sanitized });
    },
    [onFreeWordsChange, persistNewDraft]
  );

  /* -----------------------------
   * IDs 変更通知（差分時のみ発火）
   * --------------------------- */
  const lastEmittedIdsRef = useRef<{ deptId: number | null; teamId: number | null } | null>(null);
  useEffect(() => {
    const next = {
      deptId: typeof resolvedDeptId === 'number' ? resolvedDeptId : null,
      teamId: typeof resolvedTeamId === 'number' ? resolvedTeamId : null,
    };
    const prev = lastEmittedIdsRef.current;
    if (!prev || prev.deptId !== next.deptId || prev.teamId !== next.teamId) {
      lastEmittedIdsRef.current = next;
      onResolvedIdsChange?.({ departmentId: next.deptId, teamId: next.teamId });
    }
  }, [resolvedDeptId, resolvedTeamId, onResolvedIdsChange]);

  useEffect(() => {
    if (typeof resolvedTeamId !== 'number') {
      onResolvedTeamDocsetIdChange?.(undefined);
      return;
    }
    if (!master || !Array.isArray(master.teams)) {
      onResolvedTeamDocsetIdChange?.(undefined);
      return;
    }

    const hit = (master.teams as any[]).find(
      (t: any) => Number((t as any)?.team_id ?? (t as any)?.id) === resolvedTeamId
    );
    const raw = (hit as any)?.docset_id;
    if (raw === null || raw === undefined || String(raw).trim() === '') {
      onResolvedTeamDocsetIdChange?.(null);
      return;
    }

    const parsed = Number(raw);
    onResolvedTeamDocsetIdChange?.(Number.isFinite(parsed) ? parsed : null);
  }, [resolvedTeamId, master, onResolvedTeamDocsetIdChange]);

  /* -----------------------------
   * Render
   * --------------------------- */
  return (
    <div>
      <div className="header-tabs">
        <div className="add-parent-division">
          <div className="form-field select-field division">
            <select
              id="division"
              value={division}
              onChange={(e) => {
                divisionTouchedRef.current = true;
                setDivision(e.target.value);
              }}
            >
              {displayDivisionOptions.map((opt: any) => (
                <option
                  key={`${opt?.department_id ?? 'dept'}-${opt?.value ?? opt?.label}`}
                  value={opt?.value ?? opt?.label}
                >
                  {opt?.label ?? opt?.value}
                </option>
              ))}
            </select>
          </div>
        </div>

        {!division && (
          <>
            <button type="button" disabled>
              CREATE NEW
            </button>
            <button type="button" disabled>
              OVERWRITE OLD
            </button>
          </>
        )}

        {!!division && (
          <>
            <button
              type="button"
              className={`tab-button ${activeTab === 'new' ? 'active' : ''}`}
              aria-busy={modeControlsLoading}
              onClick={handleCreateNewClick}
            >
              CREATE NEW
            </button>
            <button
              type="button"
              className={`tab-button ${activeTab === 'overwrite' ? 'active' : ''}`}
              aria-busy={modeControlsLoading}
              onClick={handleOverwriteOldClick}
            >
              OVERWRITE OLD
            </button>
          </>
        )}
      </div>

      {/* 編集中に表示バー */}
      <LockWarningBar isLocked={!!isLocked} lockedByName={lockedByName ?? null} />

      {/* ここから下を「編集中は操作不可」 */}
      <div
        className="search-area-lock-container"
        style={
          isLocked
            ? {
                pointerEvents: 'none',
                opacity: 0.4,
                filter: 'grayscale(0.2)',
              }
            : undefined
        }
      >
        {/* =====================================================
         * A) タブ切替で unmount させない（DOMに残したまま隠す）
         *   - NEW は常に mounted（hidden で切替）
         *   - OVERWRITE も同様（ただし prefill が無い間は中身なし）
         * ===================================================== */}

        <div hidden={activeTab !== 'new'}>
          {division &&
            (ready && masterReadyForCurrentSelection ? (
              <SearchArea1st
                key={`new-${resolvedDeptId ?? 'x'}-${newFormResetSeq}`}
                taskMembers={taskMembers}
                // setTaskMembers をラップして「ユーザー選択＝キャッシュ保存」を実装
                setTaskMembers={handleNewTaskMembersChange}
                activeTab={activeTab as Tab}
                setActiveTab={setActiveTab}
                division={division}
                departmentId={resolvedDeptId}
                setDivision={setDivision}
                team={team}
                // setTeam をラップして「ユーザー選択＝キャッシュ保存」を実装
                setTeam={handleNewTeamChange}
                showPastTeams={showPastTeamsNew}
                setShowPastTeams={setShowPastTeamsNew}
                reporter={reporter}
                setReporter={handleNewReporterChange}
                reportType={reportType}
                setReportType={handleNewReportTypeChange}
                startDate={startDate}
                approverAM={approverAM}
                setApproverAM={handleNewApproverAMChange}
                // 日付も localStorage 保存対象にする + fields と同期して保存
                setStartDate={handleNewStartDateChange}
                endDate={endDate}
                setEndDate={handleNewEndDateChange}
                approver3={approver3}
                setApprover3={handleNewApprover3Change}
                shikimuke={shikimuke}
                setShikimuke={handleNewShikimukeChange}
                lot={lot}
                setLot={handleNewLotChange}
                enlightSyncEnabled={enlightSyncEnabled}
                validationErrors={validationErrors}
                onFieldsSnapshot={handleNewFieldsSnapshot}
                onFreeWordsChange={handleNewFreeWordsChange}
                onAttributesChange={handleNewAttributesChange}
                master={displayMaster}
                divisionLabel={String(division)}
                loginId={loginId}
                // NEW下書き（動的項目）初期値
                initialNewDraftFields={initialNewDraftFields}
                // NEW下書き（freeWords）初期値
                initialNewDraftFreeWords={initialNewDraftFreeWords}
              />
            ) : (
              <div style={{ padding: 16, color: isDark ? '#94a3b8' : '#6b7280' }}>{LOADING_MESSAGE}</div>
            ))}
        </div>

        <div hidden={activeTab !== 'overwrite'}>
          {/* OverwriteInput は「レコード選択後のみ」表示 */}
          {division &&
            (overwritePrefill ? (
              !masterReadyForCurrentSelection ? (
                <div style={{ padding: 16, color: '#6b7280' }}>{LOADING_MESSAGE}</div>
              ) : (
                <OverwriteInputSearchArea
                // 選んだ週報ごとに key を変えることで毎回マウントし直す
                key={`ow-${overwritePrefill.wr_id ?? 'new'}-d${overwriteDepartmentId ?? 'x'}`}
                master={displayMaster}
                enlightSyncEnabled={enlightSyncEnabled}
                prefill={overwritePrefill}
                onFieldsSnapshot={handleOverwriteFieldsSnapshot}
                onFreeWordsChange={handleOverwriteFreeWordsChange}
                onAttributesChange={handleOverwriteAttributesChange}
                onApproverAMChange={handleApproverAMChange}
                onApprover3Change={handleApprover3Change}
                teamValue={team}
                onTeamChange={handleTeamChange}
                onDateRangeChange={handleDateRangeChange}
                // payload は SearchArea 側で正規化してから親へ上げる
                onSearchPayloadChange={handleSearchPayloadChange}
                departmentId={overwriteDepartmentId}
                loginId={loginId}
                />
              )
            ) : modeControlsLoading ? (
              <div style={{ padding: 16, color: isDark ? '#94a3b8' : '#6b7280' }}>{LOADING_MESSAGE}</div>
            ) : (
              null
            ))}
        </div>

        <OverwriteModal
          isOpen={isOverwriteModalOpen}
          onClose={handleCloseOverwriteModal}
          onHistoryItemSelect={handleSelectOverwriteModal}
          onItemsLoaded={onHistoryLoaded}
          onSearchResults={handleOverwriteModalSearchResults}
          />
      </div>
    </div>
  );
};

export default SearchArea;
